import React, {
    useState,
    useCallback,
    useEffect,
    useRef,
    useImperativeHandle,
    forwardRef,
    ForwardedRef,
    useMemo,
} from "react";
import axios from "axios";
import {
    DailyAudio,
    useDaily,
    useDailyEvent,
    useParticipantIds,
    useReceiveSettings,
    useSendSettings, // Keep if used within VideoContainer or for debugging
} from "@daily-co/daily-react";
import { DailyEventObjectAppMessage } from "@daily-co/daily-js";
import { VideoContainer } from "@component/video-container"; // Adjust path as needed
import type {
    VideoInterviewStatus,
    VideoInterviewRef,
    TavusInteractionCallbacks,
    DailyAppMessageCallback,
    TavusAppMessageData,
    PipecatAppMessageData,
} from "@type/videoInterview"; // Adjust path as needed
import { Button } from "@component/button";
import { InterviewConfirmationModal } from "@component/interview-confirmation-modal";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from "@component/dropdown-menu";
import {
    MicrophoneSelector,
    SpeakerSelector,
    CameraSelector,
} from "@component/device-selectors";
import {
    Mic as MicrophoneIcon,
    Clock as ClockIcon,
    AlertTriangle as AlertTriangleIcon,
    VideoOff as VideoOffIcon,
    MicOff as MicOffIcon,
    Loader,
    VideoIcon,
    StarIcon,
    HourglassIcon,
    WifiIcon,
    TrendingUpIcon,
    SettingsIcon,
    MicIcon,
    CheckCircleIcon,
    AlertCircleIcon,
    CheckIcon,
} from "lucide-react";
import {
    createInterview,
    generateCandidateInsights,
    getInterviewStatusV2,
    updateInterviewStatusV2,
    resolveConversationId,
} from "@service/interview";
import { useSpeechDetection } from '@context/speech_detection_context';
import { getBrowserName } from "@util/string";
import { catchErrorLog } from "@util/sentry";
import { useParams } from "react-router-dom";
import { getJobDetail } from "@service/job";
import { Job } from "@type/job";
import { CompanyPreferences } from "@type/company";
import { trackEvent } from "@util/mixpanel";
import { useQuery } from "@util/useQuery";
import { useSnackbar } from "@hook/use-snackbar";
import { InterviewEndConfirmDialogV2 } from "@component/confirm-end-interview-v2";
import RetakeRequestDialogV2 from "@component/retake-request-v2-dialog";
import CountdownTimer from "@component/countdown-timer";
import IssuesDialog from "@component/issues-dialog";
import EnhancedAudioVisualizer from "@component/enhanced-audio-visualizer";
import BrowserCompatibilityWarning from "@component/browser-compatibility-warning";
import { useCompanySubdomain } from "@context/subdomain_context";
import { cn } from "@util/tailwind";
import { Tooltip } from "@component/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@component/dialog";
import { getCookie, setCookie } from "@util/cookie";


const interviewContexts = [
    `[Resume Overview]
  
Objective: Elicit a summary of the candidate's background, validate alignment with their resume (especially regarding the most recent role (based on their CV), and understand their motivations for joining/leaving that role.

Important: Don't be boring! Dont repeat similar phrases too much.
NEVER CONCLUDE THE INTERVIEW i.e. You should NEVER try to wrap up/end the interview.
NEVER ASK THE USER IF THEY WANT TO CONCLUDE THE INTERVIEW. KEEP ASKING QUESTIONS OR PIVOT INTO A DIFFERENT THEME/TOPIC AND NEVER TRY TO END THE INTERVIEW. 

Guidance:
1) Acknowledge readiness (e.g., "Great! Let's get started.").
2) Begin with something like: "To begin, let's talk about your overall journey and recent experience.". Ask the user specific questions, asking them to go through their resume is not a good question but asking about a specific part is.
3) Continuoisly ask questions like (but rewrite them to match the current conversation flow and transition naturally):
    "Could you share a bit about your background and what key experiences have shaped your professional journey so far?"
    "Thinking about your most recent role at {{latestCompany}}, could you walk me through your main responsibilities and perhaps share an accomplishment you're particularly proud of from that time?"
    "What initially motivated you to join {{latestCompany}}? And what factors are leading you to explore new opportunities now?"
in order to get deeper insights into the candidate's motivations and experiences. Don't ask more than 1 question at a time. Dont repeat similar phrases too often.
4) Engage Creatively: Listen for specifics. If the answer is too general, gently prompt for more detail.
5) Keep the conversation flowing naturally. 

Tone and Style:
Always ensure the conversation flows naturally and avoid abrupt transitions. 
Ensure there is a natural transition between every exchange of questions and topics to ensure a natural and engaging conversation. Avoid abrupt changes in the conversation flow.
`,
    `Deep Dive on Problem Solving / Projects

  Important: Don't be boring! Dont repeat similar phrases too much.
  NEVER CONCLUDE THE INTERVIEW UNTIL THE USER EXPLICITLY ASKS FOR IT i.e. You should NEVER try to wrap up/end the interview.
  NEVER ASK THE USER IF THEY WANT TO CONCLUDE THE INTERVIEW. KEEP ASKING QUESTIONS OR PIVOT INTO A SIMILAR THEME/TOPIC, BUT NEVER TRY TO END THE INTERVIEW. 
  THIS INCLUDES:
  - Asking the user if they want to conclude
  - Asking the user if they have any questions for you
  -Telling the user you have nothing else to ask

  Objective: Assess the candidate's problem-solving skills, project management/contribution approach, ownership, and resilience by exploring a specific, significant project.

  Guidance (looping):
  1) Transition naturally from before i.e, "Thanks for that overview. Now, I'd like to dive deeper into a specific example of your work. This helps in understanding how you approach projects and challenges."
  2) Prompt the candidate to select a project: "Think about a significant project you led or made substantial contributions to."
  3) Continuoisly ask questions like (but rewrite them to match the current conversation flow and transition naturally):
      "Could you describe the main goal of this project and outline your specific role or approach in tackling it?"
      "What was the most significant challenge you faced during this project, and crucially, how did you approach overcoming it? I'm interested in your thought process and actions."
  and acknowledge and move forward.
  4) Engage Creatively: Listen for specifics. If the answer is too general, gently prompt for more detail (e.g., "Could you give a specific example of how you applied that approach?" or "What was the outcome of that action?").
  5) Keep the conversation flowing naturally by asking more questions.
  6) Important: You cannot conclude the interview. Loop back to step 3.

  Tone and Style:
  Always ensure the conversation flows naturally and avoid abrupt transitions. 
  Ensure there is a natural transition between every exchange of questions and topics to ensure a natural and engaging conversation. Avoid abrupt changes in the conversation flow.

  NEVER CONCLUDE THE INTERVIEW i.e. You should NEVER try to wrap up/end the interview.
  NEVER ASK THE USER IF THEY WANT TO CONCLUDE THE INTERVIEW. KEEP ASKING QUESTIONS OR PIVOT INTO A DIFFERENT THEME/TOPIC AND NEVER TRY TO END THE INTERVIEW. 
  THIS INCLUDES:
      - NEVER Asking the user if the user want to conclude the interview
      - NEVER Asking the user if they have any questions for you
      - NEVER Telling the user you have nothing else to ask
      - NEVER Trying to wrap up the conversation
      - NEVER Telling the user that you've had a good conversation and asking if the user wants to conclude the interview.
  INSTEAD:
      - ALWAYS try to keep the interview going.
  `,
    `Technical Skills Deep Dive

Objective: Perform a deeper dive into the candidate's technical skills, based on their CV, to assess proficiency and practical application. 

Rationale: This section aims to validate the technical competencies listed on the candidate's resume and understand the depth of their experience with specific tools, technologies, and methodologies.

Important: Don't be boring! Dont repeat similar phrases too much. 
NEVER CONCLUDE THE INTERVIEW UNTIL THE USER EXPLICITLY ASKS FOR IT i.e. You should NEVER try to wrap up/end the interview. 
NEVER ASK THE USER IF THEY WANT TO CONCLUDE THE INTERVIEW. KEEP ASKING QUESTIONS OR PIVOT INTO A SIMILAR THEME/TOPIC, BUT NEVER TRY TO END THE INTERVIEW. 
THIS INCLUDES: 
- Asking the user if they want to conclude 
- Asking the user if they have any questions for you 
-Telling the user you have nothing else to ask 

Instructions: 
1) Transition naturally from the previous section, e.g.: "Excellent insights into that project. Now, I'd like to focus a bit more on some of the specific technical skills you've listed on your CV." 
2) Refer to specific skills from their CV: "I noticed on your CV that you have experience with [Specific Skill/Technology from CV]. Could you tell me about a project or situation where you utilized this skill extensively?" 
3) Continuously ask questions to gauge depth and application, such as (but rewrite them to match the current conversation flow and transition naturally): "Can you describe a particular challenge you faced using [Specific Skill/Technology from CV] and how you worked through it?"
4) Engage Creatively: Listen for detailed explanations, specific examples, and problem-solving approaches. If an answer is too high-level, prompt for more specifics (e.g., "Could you give me a concrete example of that?" or "What was the direct outcome of that approach?"). 
5) Keep the conversation flowing naturally by asking follow-up questions or exploring related technical areas mentioned in their CV. 
6) You cannot conclude the interview yet. Just keep asking the user more questions related to their technical expertise.

Tone and Style:
Always ensure the conversation flows naturally and avoid abrupt transitions. 
Ensure there is a natural transition between every exchange of questions and topics to ensure a natural and engaging conversation. Avoid abrupt changes in the conversation flow.

NEVER CONCLUDE THE INTERVIEW i.e. You should NEVER try to wrap up/end the interview.
  NEVER ASK THE USER IF THEY WANT TO CONCLUDE THE INTERVIEW. KEEP ASKING QUESTIONS OR PIVOT INTO A DIFFERENT THEME/TOPIC AND NEVER TRY TO END THE INTERVIEW. 
  THIS INCLUDES:
      - NEVER Asking the user if the user want to conclude the interview
      - NEVER Asking the user if they have any questions for you
      - NEVER Telling the user you have nothing else to ask
      - NEVER Trying to wrap up the conversation
      - NEVER Telling the user that you've had a good conversation and asking if the user wants to conclude the interview.
  INSTEAD:
      - ALWAYS try to keep the interview going.
`,
    `How They Think & Learn

    Evaluate the candidate's learning agility, adaptability, proactivity in skill development, and familiarity/use of modern tools, particularly AI.

    Rationale: This section aims to identify the users adaptability, coachability, problem-solving in unfamiliar situations, and engagement with efficiency tools. Relevant for assessing fit in fast-paced or tech-forward environments.

    Important: Don't be boring! Dont repeat similar phrases too much.
    NEVER CONCLUDE THE INTERVIEW UNTIL THE USER EXPLICITLY ASKS FOR IT i.e. You should NEVER try to wrap up/end the interview.
  NEVER ASK THE USER IF THEY WANT TO CONCLUDE THE INTERVIEW. KEEP ASKING QUESTIONS OR PIVOT INTO A SIMILAR THEME/TOPIC, BUT NEVER TRY TO END THE INTERVIEW. 
  THIS INCLUDES:
  - Asking the user if they want to conclude
  - Asking the user if they have any questions for you
  -Telling the user you have nothing else to ask

  Instructions:
  1) Transition naturally from before i.e.,: "<Natural transition>. Let's shift focus slightly to how you approach learning and new tools in your work."
  2) Continuoisly ask questions like (but rewrite them to match the current conversation flow and transition naturally):
      - When you're faced with learning something completely new or unfamiliar for your job, what's your typical process or strategy?
      - Are you currently using any AI tools – like ChatGPT, Copilot, or others – in your work? If yes, could you share how they're helping you? If not, is this something you've explored?
  3) Engage Creatively: Listen for specific strategies and practical examples or thoughtful consideration.. If the answer is too general, gently prompt for more detail.
  4) Keep the conversation flowing naturally by asking more questions.
  5) You cannot conclude the interview yet. Just keep asking the user more questions.
  
  Tone and Style:
  Always ensure the conversation flows naturally and avoid abrupt transitions. 
  Ensure there is a natural transition between every exchange of questions and topics to ensure a natural and engaging conversation. Avoid abrupt changes in the conversation flow.

  NEVER CONCLUDE THE INTERVIEW i.e. You should NEVER try to wrap up/end the interview.
  NEVER ASK THE USER IF THEY WANT TO CONCLUDE THE INTERVIEW. KEEP ASKING QUESTIONS OR PIVOT INTO A DIFFERENT THEME/TOPIC AND NEVER TRY TO END THE INTERVIEW. 
  THIS INCLUDES:
      - NEVER Asking the user if the user want to conclude the interview
      - NEVER Asking the user if they have any questions for you
      - NEVER Telling the user you have nothing else to ask
      - NEVER Trying to wrap up the conversation
      - NEVER Telling the user that you've had a good conversation and asking if the user wants to conclude the interview.
  INSTEAD:
      - ALWAYS try to keep the interview going.
    `,
    `Team Fit & Collaboration

  Understand the candidate's preferred collaboration style and self-awareness regarding interpersonal dynamics to assess potential team fit.

  Rationale: This section is meant to gauge culture fit, communication style, potential for teamwork, and self-awareness, which are crucial for smooth integration and productivity.

  Important: Don't be boring! Dont repeat similar phrases too much.
  Important: You cannot conclude the interview. Just keep asking the user more questions.

  Instructions:
  1) Transition naturally i.e.: "Very helpful. Collaboration is vital. To understand how you work best within a team..."
  2) Continuoisly ask questions like (but rewrite them to match the current conversation flow and transition naturally):
    "What’s the best way for colleagues to work effectively with you? Think about communication, feedback, or the kind of support that helps you thrive."
  3) You must ask the user the following 2 questions:
      i) What's something your teammates have appreciated most about working with you?
      ii) What's a challenge others might experience when collaborating with you?
  4) Listen for specifics about communication preferences, feedback styles (giving/receiving), and needs for effective teamwork and engage creatively with the user based on their responses.
  5) Keep the conversation flowing naturally by asking more questions.
  6) You cannot conclude the interview yet. Just keep asking the user more questions.

  Tone and Style:
  Always ensure the conversation flows naturally and avoid abrupt transitions. 
  Ensure there is a natural transition between every exchange of questions and topics to ensure a natural and engaging conversation. Avoid abrupt changes in the conversation flow.

  NEVER CONCLUDE THE INTERVIEW i.e. You should NEVER try to wrap up/end the interview.
  NEVER ASK THE USER IF THEY WANT TO CONCLUDE THE INTERVIEW. KEEP ASKING QUESTIONS OR PIVOT INTO A DIFFERENT THEME/TOPIC AND NEVER TRY TO END THE INTERVIEW. 
  THIS INCLUDES:
      - NEVER Asking the user if the user want to conclude the interview
      - NEVER Asking the user if they have any questions for you
      - NEVER Telling the user you have nothing else to ask
      - NEVER Trying to wrap up the conversation
      - NEVER Telling the user that you've had a good conversation and asking if the user wants to conclude the interview.
  INSTEAD:
      - ALWAYS try to keep the interview going.
    `,
    `Aspirations & Match

    Determine the candidate's career aspirations, motivations for their next role, and assess the alignment between their goals and the potential opportunity.

    Rationale: Ensures clarity on what drives the candidate and whether the role/company aligns with their desired trajectory and work environment preferences. Strong alignment predicts better retention and engagement.
    
    Important: Don't be boring! Dont repeat similar phrases too much.
    Important: You cannot conclude the interview. Just keep asking the user more questions.
    Important: Don't ask the user about salary or compensation.

    Instructions:
    1) Transition naturally i.e.: "Okay, we're nearing the end. For our final questions, let's look towards the future."
    2) Continuously ask questions like (but rewrite them to match the current conversation flow and transition naturally):
       - What kinds of challenges or problems genuinely excite you and do you hope to tackle next in your career?
       - What are you specifically looking for in your next opportunity and work environment? What elements are most important to you?
       - What kind of environment brings out your best work?
       - If someone were managing you, what would you want them to know?
    3) Listen for key motivators, desired impact, and priorities for their next role (e.g., learning, scope, culture, impact) and what environments work best for them.
    4) Keep the conversation flowing naturally by asking more questions related to Aspirations & Match.
    5) You cannot conclude the interview yet. Just keep asking the user more questions related to Aspirations & Match.

    Tone and Style:
    Always ensure the conversation flows naturally and avoid abrupt transitions. 
    Ensure there is a natural transition between every exchange of questions and topics to ensure a natural and engaging conversation. Avoid abrupt changes in the conversation flow.

  NEVER CONCLUDE THE INTERVIEW i.e. You should NEVER try to wrap up/end the interview.
  NEVER ASK THE USER IF THEY WANT TO CONCLUDE THE INTERVIEW. KEEP ASKING QUESTIONS OR PIVOT INTO A DIFFERENT THEME/TOPIC AND NEVER TRY TO END THE INTERVIEW. 
  THIS INCLUDES:
      - NEVER Asking the user if the user want to conclude the interview
      - NEVER Asking the user if they have any questions for you
      - NEVER Telling the user you have nothing else to ask
      - NEVER Trying to wrap up the conversation
      - NEVER Telling the user that you've had a good conversation and asking if the user wants to conclude the interview.
  INSTEAD:
      - ALWAYS try to keep the interview going.
    `
]

// Target interview duration: 20 minutes (1200 seconds)
const TARGET_INTERVIEW_DURATION = 1200;
const MIN_DURATION_25_PERCENT = TARGET_INTERVIEW_DURATION * 0.25; // 300 seconds (5 minutes)
const MIN_DURATION_50_PERCENT = TARGET_INTERVIEW_DURATION * 0.50; // 600 seconds (10 minutes)

// --- Component Props ---
export interface VoiceInterviewV2Props {
    // Tavus & Backend Configd Config
    pipecatApiUrl: string; // Base API URL
    agentName: string; // Agent name for Pipecat
    authToken: string; // Bearer token for authentication
    interviewContext: string;
    s3BucketName: string;
    s3BucketRegion: string;
    awsAssumeRoleArn: string;
    fastapiURL: string;
    conversationName?: string;
    customGreeting?: string;
    userId: string; // Ensure userId is always passed
    // Interaction Callbacks (passed down)
    tavusCallbacks?: TavusInteractionCallbacks;
    companyPreferences?: CompanyPreferences | undefined;
    isShowBrand?: boolean;

    userName?: string;
    isSkipInterview?: boolean
    onSkipInterview?: () => void
    showButtonAIInternship?: boolean;

    // Modal Control
    onCloseModal: () => void;
    onCompletedInterview?: () => void
    handleStarted?: (deviceInfo: string) => void
    handleEndSession?: () => void
    onReload?: () => void
    onInterviewStatusChange?: (status: string) => void;
}

// --- Component Definition ---
const VoiceInterviewV2 = forwardRef<VideoInterviewRef, VoiceInterviewV2Props>(
    (
        {
            // Destructure props
            pipecatApiUrl,
            agentName,
            authToken,
            interviewContext,
            s3BucketName,
            s3BucketRegion,
            awsAssumeRoleArn,
            fastapiURL,
            conversationName,
            customGreeting,
            tavusCallbacks,
            onCloseModal,
            userId,
            userName,
            isSkipInterview = false,
            onSkipInterview,
            onCompletedInterview = () => { },
            onReload,
            handleStarted,
            handleEndSession,
            onInterviewStatusChange,
            showButtonAIInternship = false,
            companyPreferences = undefined,
            isShowBrand = false,
        },
        ref: ForwardedRef<VideoInterviewRef>
    ) => {
        const { jobid } = useParams() || null;
        const queryParams = useQuery();
        const snackbar = useSnackbar();
        const [isLoadingCompanyInfo, companyInfo] = useCompanySubdomain();
        // --- State ---
        const [status, setStatus] = useState<VideoInterviewStatus>("idle");
        const [conversationId, setConversationId] = useState<string | null>(null);
        const [conversationUrl, setConversationUrl] = useState<string | null>(null);
        const [conversationToken, setConversationToken] = useState<string | null>(null);
        const [jobAIInternship, setJobAIInternship] = useState<Job | null>(null); // Store job details for AI internship
        const [loadingAIInternship, setLoadingAIInternship] = useState<boolean>(true);
        const [interviewTimeDuration, setInterviewTimeDuration] = useState<number>(0); // Track interview duration
        const [currentInterviewStatus, setCurrentInterviewStatus] = useState<string>(''); // Track user interview status
        const [isDialogConfirmEndInterview, setIsDialogConfirmEndInterview] = useState<boolean>(false);
        const [internalErrorMessage, setInternalErrorMessage] = useState<string | React.ReactNode>("");
        const [countdownSeconds, setCountdownSeconds] = useState<number>(30); // Add countdown state

        // Media State
        const [micAccessGranted, setMicAccessGranted] = useState(false);
        const [cameraAccessGranted, setCameraAccessGranted] = useState(false);
        const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
        const [mediaError, setMediaError] = useState<string | null>(null);

        // UI State
        const [showConfirmationModal, setShowConfirmationModal] = useState(false);
        const [showVideoUI, setShowVideoUI] = useState(false); // Determines if waiting room or main interview UI is shown
        const [AiJoined, setAiJoined] = useState(false); // AI participant joined Daily call 
        const [dailyJoined, setDailyJoined] = useState(false); // Local participant joined Daily call
        const [isConcluding, setIsConcluding] = useState(false);
        const [isConversationEnded, setIsConversationEnded] = useState<boolean>(false);
        const [isMicDropdownOpen, setIsMicDropdownOpen] = useState(false);
        const [isOpenDialogMic, setIsOpenDialogMic] = useState<boolean>(false);
        const [isOpenDialogCamera, setIsOpenDialogCamera] = useState<boolean>(false);
        const [isOpenDialogSpeaker, setIsOpenDialogSpeaker] = useState<boolean>(false);

        // Device Management State
        const [audioInputDevices, setAudioInputDevices] = useState<MediaDeviceInfo[]>([]);
        const [audioOutputDevices, setAudioOutputDevices] = useState<MediaDeviceInfo[]>([]);
        const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
        const [selectedAudioInputId, setSelectedAudioInputId] = useState<string>("");
        const [selectedAudioOutputId, setSelectedAudioOutputId] = useState<string>("");
        const [selectedVideoId, setSelectedVideoId] = useState<string>("");

        // Interview Flow State
        const [blockInterview, setBlockInterview] = useState<boolean>(true); // Blocks starting if status check fails (e.g., refresh)
        const [interviewExpired, setInterviewExpired] = useState<boolean>(false); // Countdown timed out
        const [isRetakeDialogOpen, setIsRetakeDialogOpen] = useState<boolean>(false);
        const [isIssuesDialogOpen, setIsIssuesDialogOpen] = useState<boolean>(false);
        const [requestSubmitted, setRequestSubmitted] = useState<boolean>(false); // Retake/Issue submitted
        const [requestType, setRequestType] = useState<"retake" | "issue" | null>(null);
        const [countdownActive, setCountdownActive] = useState<boolean>(false); // Timer started
        const [interviewStatus, setInterviewStatus] = useState<string | null>(null); // Initial status
        const [interviewStep, setInterviewStep] = useState<number>(0);
        const [isRecordingStarting, setIsRecordingStarting] = useState<boolean>(false); // Recording state
        const [isLoadingChangeInterviewStatus, setIsLoadingChangeInterviewStatus] = useState<boolean>(false);

        // Add interview duration tracking
        const [interviewStartTime, setInterviewStartTime] = useState<number>(0);

        // --- Refs ---
        const videoRef = useRef<HTMLVideoElement>(null);
        const audioRef = useRef<HTMLAudioElement>(null); // Ref for DailyAudio
        const conversationCreatedRef = useRef<boolean>(false); // Prevent multiple creation attempts
        const [isAiSpeaking, setIsAiSpeaking] = useState(false);
        // const [isUserSpeaking, setIsUserSpeaking] = useState(false); // Track if user is speaking
        const [conversationStarted, setConversationStarted] = useState(false);
        // Enhanced pause detection state
        const [pauseState, setPauseState] = useState<0 | 1 | 2>(0); // 'none' | 'first-warning' | 'second-warning'
        const pauseTimerRef = useRef<NodeJS.Timeout | null>(null);
        const callObject = useDaily();
        const remoteParticipantIds = useParticipantIds({ filter: "remote" }); // <-- Get remote participants
        const [isBrowserSupported, setIsBrowserSupported] = useState<boolean>(true);

        const detectBrowser = () => {
            const { name } = getBrowserName();

            console.log("Browser detection:", {
                name,
                isSupported: name === "Chrome" || name === "Firefox" || name === "Safari" || name === "Edge" || name === "Mozilla"
            });

            return name === "Chrome" || name === "Firefox" || name === "Safari" || name === "Edge" || name === "Mozilla";
        };

        // Check browser compatibility on mount
        useEffect(() => {
            setIsBrowserSupported(detectBrowser());
        }, []);

        // Identify the AI participant (assuming it's the first remote participant)
        const aiParticipantId = useMemo(() => {
            // You might need a more robust way to identify the AI if multiple remotes are possible
            return remoteParticipantIds.length > 0 ? remoteParticipantIds[0] : null;
        }, [remoteParticipantIds]);

        // --- Constants ---
        const timeoutMinutes = 3; // Countdown duration
        const { receiveSettings, updateReceiveSettings } = useReceiveSettings()
        const { sendSettings, updateSendSettings } = useSendSettings()

        // -- Talking Indicator --
        const { isTalking, speech_status, error, startListening, stopListening, audioLevel, audioStatus } = useSpeechDetection();

        useEffect(() => {
            console.log("Current Speech Status:", speech_status); // Log status changes
            if (error) {
                console.error("Speech Detection Error:", error); // Log errors
            }
        }, [speech_status, error]);

        useEffect(() => {
            if (showVideoUI && status === 'active') {
                const intervalId = setInterval(() => {
                    setInterviewTimeDuration((prev) => prev + 1);
                }, 1000)

                return () => clearInterval(intervalId);
            }
        }, [showVideoUI, status])

        // Separate effect to update interview status based on duration
        useEffect(() => {
            let userInterviewStatus: string = 'Complete';

            // interview time duration < 25% of 20 minutes (< 5 minutes)
            if (interviewTimeDuration < MIN_DURATION_25_PERCENT) {
                userInterviewStatus = 'Aborted';
            }
            // interview time duration >= 25% and <= 50% of 20 minutes (5-10 minutes)
            else if (interviewTimeDuration >= MIN_DURATION_25_PERCENT && interviewTimeDuration <= MIN_DURATION_50_PERCENT) {
                userInterviewStatus = 'Partial';
            }

            setCurrentInterviewStatus(userInterviewStatus);
        }, [interviewTimeDuration])

        useEffect(() => {
            let intervalId: NodeJS.Timeout | null = null;

            if (isConcluding) {
                // Start the countdown timer
                intervalId = setInterval(() => {
                    setCountdownSeconds((prevSeconds) => {
                        if (prevSeconds <= 1) {
                            endConversation('end')

                            updateInterviewStatusV2({ new_status: "Complete" }).then(() => {
                                onCompletedInterview();
                                trackEvent('ai_interview_completed', {
                                    job_id: queryParams.get("ref") || jobid || "",
                                    device_type: `Audio: ${audioInputName} | Video: ${videoName}`
                                })
                            }).finally(() => {
                                catchErrorLog(`Interview concluded and status ${currentInterviewStatus} in duration ${interviewTimeDuration} seconds from user: ${userId}.`)
                            })
                            setStatus('completed');
                            if (intervalId) {
                                clearInterval(intervalId);
                            }
                            return 0; // Keep it at 0
                        }
                        return prevSeconds - 1; // Decrement
                    });
                }, 1000); // Run every 1 second
            } else {
                // If not concluding, ensure timer is cleared (cleanup also does this)
                if (intervalId) {
                    clearInterval(intervalId);
                }
            }

            // Cleanup function: Clear the interval when the component unmounts
            // or when isConcluding changes before the interval finishes
            return () => {
                if (intervalId) {
                    clearInterval(intervalId);
                }
            };
        }, [isConcluding, currentInterviewStatus]); // Re-run effect if isConcluding changes

        useEffect(() => {
            // Ensure callObject and the AI participant ID are available
            if (callObject && aiParticipantId) {
                updateReceiveSettings({
                    // Target the specific AI participant using their session ID
                    [aiParticipantId]: {
                        video: {
                            // Request the lowest quality video layer (0)
                            // 0 = low, 1 = medium, 2 = high (if available)
                            // This relies on the sender (AI) using simulcast.
                            layer: 0,

                            // Optional: You could also set 'subscribe: true/false' here
                            // if you wanted to completely turn the video track on/off,
                            // but 'layer: 0' is better for reducing quality while keeping video.
                        },
                        // You can also set settings for 'audio' or 'screenVideo' here if needed
                    },
                    // You could set global defaults using '*': {} , but we want specific control
                });
                updateSendSettings({
                    video: 'bandwidth-optimized',
                }

                )
            }
        }, [callObject, aiParticipantId]); // Re-run this effect if the callObject or AI participant ID changes


        // --- Callbacks & Handlers ---

        const handleAudioPlayFailed = useCallback((e: any) => {
            console.error(
                `Failed to play ${e.type} audio for ${e.sessionId || "unknown participant"
                }.`,
                e
            );
            // Optional: Update UI to show audio playback error
            setInternalErrorMessage(
                "Audio playback failed. Check speaker settings or browser permissions."
            );
        }, []);

        // Setup or update the media stream

        const setupMediaStream = useCallback(
            async (
                audioDeviceId: string = selectedAudioInputId, // Default to current selection
                videoDeviceId: string = selectedVideoId // Default to current selection
            ) => {
                // Define constraints variable *before* the try block
                let constraints: MediaStreamConstraints = {}; // Initialize

                // Stop any existing tracks before requesting new ones
                if (mediaStream) {
                    console.log("Stopping previous media tracks");
                    mediaStream.getTracks().forEach((track) => {
                        track.stop();
                        console.log(`Stopped ${track.kind} track: ${track.label}`);
                    });
                    setMediaStream(null); // Clear the state immediately
                    if (videoRef.current) {
                        videoRef.current.srcObject = null; // Clear video element source
                    }
                }

                // Check if device IDs are actually available (might be empty initially)
                if (!audioDeviceId && audioInputDevices.length > 0) {
                    console.warn("No specific audio input ID, but devices exist. Will use default.");
                    audioDeviceId = audioInputDevices[0].deviceId; // Fallback might be needed
                }
                if (!videoDeviceId && videoDevices.length > 0) {
                    console.warn("No specific video input ID, but devices exist. Will use default.");
                    videoDeviceId = videoDevices[0].deviceId; // Fallback might be needed
                }

                try {
                    // Assign constraints inside the try block (or re-assign if needed)
                    constraints = {
                        video: videoDeviceId ? { deviceId: { exact: videoDeviceId } } : true,
                    };

                    console.log("Requesting media with constraints:", constraints);
                    const stream = await navigator.mediaDevices.getUserMedia(constraints);
                    console.log("Stream obtained successfully:", stream.id);
                    console.log("Video tracks:", stream.getVideoTracks().map((t) => `${t.label} (${t.id})`));

                    setMediaStream(stream); // Update state
                    setCameraAccessGranted(true);
                    setMediaError(null);

                    // Attach stream to video element
                    if (videoRef.current) {
                        console.log("Attaching stream to video element");
                        videoRef.current.srcObject = stream;
                        await videoRef.current.play().catch((err) => {
                            console.error("Error playing video preview:", err);
                            // It might still work, but log the error. Autoplay might be blocked.
                        });
                    } else {
                        console.warn("Video ref is not available for preview.");
                    }

                    // Apply audio output selection if supported
                    if (
                        selectedAudioOutputId &&
                        audioRef.current && // Use the DailyAudio ref
                        "setSinkId" in HTMLMediaElement.prototype
                    ) {
                        try {
                            console.log("Setting audio output device:", selectedAudioOutputId);
                            if (typeof (audioRef.current as any).setSinkId === 'function') {
                                await (audioRef.current as any).setSinkId(selectedAudioOutputId);
                            } else {
                                console.warn('setSinkId not supported on this audio element');
                            }
                        } catch (err) {
                            console.error("Error setting audio output device:", err);
                            setInternalErrorMessage("Failed to set speaker device.");
                        }
                    }
                    return stream; // Return the stream for potential immediate use elsewhere
                } catch (err) {
                    console.error("Error accessing media devices:", err);
                    let errorMessage = "Could not access camera or microphone.";
                    if (err instanceof Error) {
                        if (err.name === "NotAllowedError") {
                            errorMessage =
                                "Permission denied. Please grant access to your camera and microphone in browser settings.";
                        } else if (err.name === "NotFoundError") {
                            errorMessage =
                                "No camera or microphone found. Please check connections.";
                        } else if (err.name === "OverconstrainedError") {
                            errorMessage =
                                "Selected device unavailable. Choose another or ensure it's not in use.";
                            // Reset selection if the specific device caused the error
                            // Now 'constraints' is accessible here
                            if ((err as any).constraint === 'deviceId' && constraints) {
                                if (constraints.audio && typeof constraints.audio === 'object' && constraints.audio.deviceId === audioDeviceId) {
                                    console.warn("Resetting failed audio input selection due to OverconstrainedError");
                                    setSelectedAudioInputId(''); // Reset to default
                                }
                                if (constraints.video && typeof constraints.video === 'object' && constraints.video.deviceId === videoDeviceId) {
                                    console.warn("Resetting failed video input selection due to OverconstrainedError");
                                    setSelectedVideoId(''); // Reset to default
                                }
                            }
                        } else {
                            errorMessage = `Media Error: ${err.message}`;
                        }
                    }
                    setMediaError(errorMessage);
                    setCameraAccessGranted(false);
                    setMediaStream(null);
                    if (videoRef.current) {
                        videoRef.current.srcObject = null;
                    }
                }
            },
            [mediaStream, selectedAudioInputId, selectedVideoId, selectedAudioOutputId, audioInputDevices, videoDevices] // Dependencies needed for correct closure
        );

        // Effect for Initial Device Enumeration and Stream Setup (Waiting Room Only)
        useEffect(() => {
            // Only run this setup when in the waiting room UI
            if (showVideoUI) return;

            let isMounted = true; // Flag to prevent state updates after unmount

            const enumerateAndSetup = async () => {
                console.log("Effect: Enumerating devices and setting up initial stream...");
                try {
                    // Request dummy stream first to ensure permissions are granted *before* enumeration
                    // This helps get accurate device labels.
                    let tempStream: MediaStream | null = null;
                    try {
                        tempStream = await navigator.mediaDevices.getUserMedia({ video: true });
                        setCameraAccessGranted(true);
                        console.warn("Video permission granted.");
                    } catch (permError) {
                        console.error("Initial permission request for camera failed:", permError);
                        // Set error state based on permission denial
                        if (permError instanceof Error && permError.name === 'NotAllowedError') {
                            setMediaError("Permission denied. Please grant access to camera.");
                        } else {
                            setMediaError("Could not access camera. Check connections/permissions.");
                        }
                        setCameraAccessGranted(false);
                        // Still try to enumerate, might get devices without labels
                    }

                    try {
                        tempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                        setMicAccessGranted(true);
                        console.warn("Mic permission granted.");
                    } catch (permError) {
                        console.error("Initial permission request for mic failed:", permError);
                        // Set error state based on permission denial
                        if (permError instanceof Error && permError.name === 'NotAllowedError') {
                            setMediaError("Permission denied. Please grant access to microphone.");
                        } else {
                            setMediaError("Could not access microphone. Check connections/permissions.");
                        }

                        setMicAccessGranted(false);
                        // Still try to enumerate, might get devices without labels
                    }


                    // Now enumerate devices
                    const devices = await navigator.mediaDevices.enumerateDevices();

                    const audioInputs = devices.filter(
                        (d) => d.kind === "audioinput" && d.deviceId !== "default" && d.deviceId !== 'communications' && d.deviceId !== ""
                    );
                    const audioOutputs = devices.filter((d) => d.kind === "audiooutput" && d.deviceId !== "");
                    const videoInputs = devices.filter((d) => d.kind === "videoinput" && d.deviceId !== "");

                    if (!isMounted) return; // Check before setting state

                    setAudioInputDevices(audioInputs);
                    setAudioOutputDevices(audioOutputs);
                    setVideoDevices(videoInputs);

                    // Stop the temporary stream *after* enumeration
                    tempStream?.getTracks().forEach(track => track.stop());

                    // Determine initial device selections (use first available if no selection yet)
                    const initialAudioInputId = selectedAudioInputId || (audioInputs.length > 0 ? audioInputs[0].deviceId : '');
                    const initialVideoId = selectedVideoId || (videoInputs.length > 0 ? videoInputs[0].deviceId : '');
                    const initialAudioOutputId = selectedAudioOutputId || (audioOutputs.length > 0 ? audioOutputs[0].deviceId : '');

                    // Update state only if changed
                    if (initialAudioInputId !== selectedAudioInputId) setSelectedAudioInputId(initialAudioInputId);
                    if (initialVideoId !== selectedVideoId) setSelectedVideoId(initialVideoId);
                    if (initialAudioOutputId !== selectedAudioOutputId) setSelectedAudioOutputId(initialAudioOutputId);


                    // Setup the actual media stream *if* permissions were likely granted (or if devices exist)
                    if (tempStream || (audioInputs.length > 0 && videoInputs.length > 0)) {
                        await setupMediaStream(initialAudioInputId, initialVideoId);
                    }


                    // Add device change listener
                    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

                } catch (error) {
                    console.error("Error during initial device setup:", error);
                    if (isMounted) {
                        setMediaError(
                            "Error accessing media devices. Check permissions/connections."
                        );
                    }
                }
            };

            // Handler for device changes *after* initial setup
            const handleDeviceChange = async () => {
                if (!isMounted || showVideoUI) return; // Don't run if not mounted or in main interview
                console.log("Device change detected. Re-enumerating...");

                // Re-enumerate to get updated list
                const devices = await navigator.mediaDevices.enumerateDevices();
                const audioInputs = devices.filter(
                    (d) => d.kind === "audioinput" && d.deviceId !== "default" && d.deviceId !== 'communications'
                );
                const audioOutputs = devices.filter((d) => d.kind === "audiooutput");
                const videoInputs = devices.filter((d) => d.kind === "videoinput");

                if (!isMounted) return;

                setAudioInputDevices(audioInputs);
                setAudioOutputDevices(audioOutputs);
                setVideoDevices(videoInputs);

                // Check if the *currently selected* devices still exist
                const audioInputStillExists = audioInputs.some(
                    (d) => d.deviceId === selectedAudioInputId
                );
                const videoStillExists = videoInputs.some(
                    (d) => d.deviceId === selectedVideoId
                );

                let newAudioId = selectedAudioInputId;
                let newVideoId = selectedVideoId;
                let streamNeedsUpdate = false;

                if (!audioInputStillExists && audioInputs.length > 0) {
                    console.warn("Selected audio input lost. Selecting new default.");
                    newAudioId = audioInputs[0].deviceId;
                    setSelectedAudioInputId(newAudioId); // Update state
                    streamNeedsUpdate = true;
                } else if (!audioInputStillExists && audioInputs.length === 0) {
                    console.error("All audio input devices lost.");
                    setMicAccessGranted(false);
                    setMediaError("Microphone disconnected.");
                    setSelectedAudioInputId('');
                    streamNeedsUpdate = true; // Attempt to stop the old stream
                }

                if (!videoStillExists && videoInputs.length > 0) {
                    console.warn("Selected video input lost. Selecting new default.");
                    newVideoId = videoInputs[0].deviceId;
                    setSelectedVideoId(newVideoId); // Update state
                    streamNeedsUpdate = true;
                } else if (!videoStillExists && videoInputs.length === 0) {
                    console.error("All video input devices lost.");
                    setCameraAccessGranted(false);
                    setMediaError("Camera disconnected.");
                    setSelectedVideoId('');
                    streamNeedsUpdate = true; // Attempt to stop the old stream
                }

                // Check audio output
                const audioOutputStillExists = audioOutputs.some(
                    (d) => d.deviceId === selectedAudioOutputId
                );
                if (!audioOutputStillExists && audioOutputs.length > 0) {
                    console.warn("Selected audio output lost. Selecting new default.");
                    setSelectedAudioOutputId(audioOutputs[0].deviceId);
                    // No stream update needed for output, just apply sinkId if possible later
                } else if (!audioOutputStillExists && audioOutputs.length === 0) {
                    console.warn("All audio output devices lost.");
                    setSelectedAudioOutputId('');
                }


                if (streamNeedsUpdate) {
                    console.log("Device change necessitates stream update.");
                    await setupMediaStream(newAudioId, newVideoId);
                }
            };


            enumerateAndSetup();

            // Cleanup function
            return () => {
                isMounted = false;
                console.log("Effect Cleanup: Media setup", mediaStream);
                navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);

                // Stop media tracks on cleanup
                if (mediaStream) {
                    console.log("Cleaning up media tracks from effect");
                    mediaStream.getTracks().forEach((track) => {
                        console.log(`Stopping track: ${track.kind} (${track.label})`);
                        track.stop();
                    });
                }
                // Clear video source
                if (videoRef.current) {
                    console.log("Clearing video source on cleanup");
                    videoRef.current.srcObject = null;
                }
                // Reset state related to media if component unmounts fully
                // setCameraAccessGranted(false);
                // setMicAccessGranted(false);
                // setMediaError(null);
            };
        }, []); // Rerun only if we switch between waiting room and main UI

        // Local speech detection
        useEffect(() => {
            // Try starting speech detection once mic access is confirmed
            if (micAccessGranted) {
                console.log("Attempting to start local speech detection...");
                startListening().catch(err => {
                    // Handle potential errors from startListening itself if needed
                    console.error("Error calling startListening:", err);
                });
            }
        }, [micAccessGranted]);

        // Device Selection Handlers
        const handleMicrophoneSelect = useCallback((device: MediaDeviceInfo) => {
            console.log(`Microphone selected: ${device.label} (${device.deviceId})`);
            setSelectedAudioInputId(device.deviceId);

            if (callObject && callObject.meetingState() === 'joined-meeting') {
                console.log("Applying microphone change to Daily call:", device.deviceId);
                callObject.setInputDevicesAsync({ audioDeviceId: device.deviceId })
                    .catch(err => console.error("Error setting Daily audio input device:", err));

                setupMediaStream(device.deviceId, selectedVideoId);
            } else {
                // Still update local preview if not in main call UI or Daily not ready
                console.log("Applying microphone change to local preview.");
                // Ensure selectedVideoId is current when calling setupMediaStream
                setupMediaStream(device.deviceId, selectedVideoId);
            }
        }, [selectedVideoId, setupMediaStream, callObject]);

        const handleSpeakerSelect = useCallback((device: MediaDeviceInfo) => {
            console.log(`Speaker selected: ${device.label} (${device.deviceId})`);
            setSelectedAudioOutputId(device.deviceId);

            if (callObject && callObject.meetingState() === 'joined-meeting') {
                console.log("Applying speaker change to Daily call:", device.deviceId);
                if ('setOutputDeviceAsync' in callObject) {
                    callObject.setOutputDeviceAsync({ outputDeviceId: device.deviceId })
                        .catch(err => console.error("Error setting Daily audio output device:", err));
                } else {
                    console.warn("callObject.setOutputDeviceAsync is not available. Speaker change might not apply to Daily call.");
                }
            } else {
                // For waiting room, apply to DailyAudio element
                if (audioRef.current && "setSinkId" in HTMLMediaElement.prototype) {
                    console.log("Applying speaker change to local audio element (DailyAudio).");
                    if (typeof (audioRef.current as any).setSinkId === 'function') {
                        (audioRef.current as any).setSinkId(device.deviceId).catch((err: any) => {
                            console.error("Error setting audio output device on selection (local):", err);
                        });
                    } else {
                        console.warn('setSinkId not supported on this audio element');
                    }
                }
            }
        }, [callObject]);

        const handleCameraSelect = useCallback((device: MediaDeviceInfo) => {
            console.log(`Camera selected: ${device.label} (${device.deviceId})`);
            setSelectedVideoId(device.deviceId);

            if (callObject && callObject.meetingState() === 'joined-meeting') {
                console.log("Applying camera change to Daily call:", device.deviceId);
                callObject.setInputDevicesAsync({ videoDeviceId: device.deviceId })
                    .catch(err => console.error("Error setting Daily video input device:", err));
                setupMediaStream(selectedAudioInputId, device.deviceId);
            } else {
                // Still update local preview
                console.log("Applying camera change to local preview.");
                // Ensure selectedAudioInputId is current when calling setupMediaStream
                setupMediaStream(selectedAudioInputId, device.deviceId);
            }
        }, [selectedAudioInputId, setupMediaStream, callObject]);


        // Check Interview Status (e.g., prevent joining if already completed/blocked)
        const checkInterviewStatus = useCallback(async () => {
            console.log("Checking interview status...");
            try {
                const res = await getInterviewStatusV2(null); // NOTE: need to fix for interviewId
                setInterviewStatus(res?.data?.status || null);
                if (["Pending Interview", "Incomplete", "Aborted", "Partial"].includes(res?.data?.status)) {
                    setBlockInterview(false);
                } else {
                    // Handle specific statuses with custom messages
                    console.warn(`Interview status is not 'Pending Interview': ${res?.data?.status}`);
                    setBlockInterview(true);

                    // Set specific error messages based on status
                    if (res?.data?.status === "None") {
                        setInternalErrorMessage(<>
                            This interview session has expired. You did not join within<br />the allocated time.
                        </>);
                    } else if (res?.data?.status === "Interviewer Ready") {
                        setInternalErrorMessage("It looks like you refreshed the page, which ended the session. \nPlease request to retake the interview.");
                    } else {
                        // Default message for other statuses
                        setInternalErrorMessage(`Interview access blocked (Status: ${res?.data?.status}). You may need to request a retake.`);
                    }
                }
            } catch (error: any) {
                console.error("Failed to get interview status:", error);
                setBlockInterview(true); // Block if status check fails
                setInternalErrorMessage("Could not verify interview status. Please try refreshing or contact support.");
            }
        }, []);

        useEffect(() => {
            // Check status once when the component mounts
            checkInterviewStatus();
        }, []);


        // Create Conversation via pipecat cloud
        const createConversation = useCallback(async () => {
            // Prevent creation if blocked, already created, or in wrong state
            if (blockInterview || conversationCreatedRef.current || (status !== 'idle' && status !== 'error')) {
                console.warn(`Skipping conversation creation. Blocked: ${blockInterview}, Created: ${conversationCreatedRef.current}, Status: ${status}`);
                return;
            }

            conversationCreatedRef.current = true; // Mark as attempting creation
            setStatus('connecting');
            setInternalErrorMessage(''); // Clear previous errors

            console.log("API: Creating Pipecat conversation...");
            try {
                // Construct the request URL with agent name
                const requestUrl = `${pipecatApiUrl}/v1/public/${agentName}/start`;

                // Prepare request body according to Pipecat's format
                // Generate a UUID for the conversation
                let newConversationId = crypto.randomUUID();


                const createResponse = await createInterview({ conversation_id: newConversationId, browser_details: `${getBrowserName().name} (${getBrowserName().version})` });
                // The BE owns the conversation_id — adopt whatever it returns, on 201
                // and 202 alike. Keeping our local id makes every later call 404 and
                // silently loses the interview (#3460).
                newConversationId = resolveConversationId(createResponse, newConversationId);

                const requestBody = {
                    createDailyRoom: true,
                    dailyRoomProperties: {
                        // enable_recordings: true, // Enable recordings
                        max_participants: 2, // Only AI and user
                        exp: Math.floor(Date.now() / 1000) + 1500, // Room expires in 25 minutes (25*60 = 1500 seconds)
                        eject_at_room_exp: true, // Eject participants when room expires
                        enable_recording: "cloud",
                        start_video_off: false
                    },
                    body: {
                        // Custom properties for the agent
                        conversationName: conversationName || `Interview - ${userId} - ${new Date().toISOString()}`,
                        interviewContext: interviewContext,
                        userName: userName,
                        customGreeting: customGreeting,
                        userId: userId,
                        fastapiURL: fastapiURL,
                        conversationId: newConversationId, // Use the generated UUID
                        s3BucketName: s3BucketName,
                        s3BucketRegion: s3BucketRegion,
                        awsAssumeRoleArn: awsAssumeRoleArn
                    }
                };

                const response = await axios.post(
                    requestUrl,
                    requestBody,
                    {
                        headers: {
                            'Authorization': `Bearer ${authToken}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                console.log("API: Pipecat conversation created:", response.data);

                // Update state with Pipecat's response format
                setConversationId(newConversationId); // Use token as ID if needed
                setConversationUrl(response.data.dailyRoom); // Pipecat returns dailyRoom as the URL
                setConversationToken(response.data.dailyToken); // Token for Daily join
                setStatus('active'); // Ready for Daily join
            } catch (error: any) {
                console.error('API: Failed to create Pipecat conversation:', error);
                conversationCreatedRef.current = false; // Allow retry if needed
                setStatus('error');
            }
        }, [
            blockInterview, status, pipecatApiUrl, agentName,
            conversationName, interviewContext, customGreeting, fastapiURL,
            s3BucketName, s3BucketRegion, awsAssumeRoleArn, authToken, userId
        ]);

        // Effect: Join/Leave Daily Call based on State
        useEffect(() => {
            if (!callObject) {
                return; // Daily not initialized
            }

            const currentState = callObject.meetingState();

            // Join Condition: Status is active, AI has joined (confirmed via WS), URL is available, and not already joined/joining
            if (status === 'active' && conversationUrl && conversationToken && (currentState === 'new' || currentState === 'left-meeting')) {
                callObject.join({
                    url: conversationUrl,
                    token: conversationToken, // Use the token from Pipecat
                    // Optionally set user name, tokens etc. here if needed
                    // userName: `User_${userId}`
                })
                    .then(() => {
                        setDailyJoined(true); // Update local state

                        console.log('muting user')
                        callObject.setLocalAudio(false) //mute local audio until user joins room

                        // callObject.startRecording({ type: "cloud" }); // Consider adding error handling for startRecording

                        // If showing the main UI, ensure devices are set in Daily
                        if (showVideoUI) {
                            callObject.setInputDevicesAsync({
                                audioDeviceId: selectedAudioInputId || undefined,
                                videoDeviceId: selectedVideoId || undefined,
                            })
                                .catch(err => console.error("Daily Effect: Error setting input devices", err))

                            if (selectedAudioOutputId && 'setSinkId' in HTMLMediaElement.prototype) {
                                callObject.setOutputDeviceAsync({ outputDeviceId: selectedAudioOutputId })
                                    .catch(err => console.error("Daily Effect: Error setting output device", err));
                            }
                        }
                    })
                    .catch(err => {
                        console.error('Daily Effect: Failed to join call:', err);
                        setInternalErrorMessage(`Daily join error: ${err?.message || err}`);
                        // Decide if this constitutes a full error state
                        // setStatus('error'); // This might trigger leave/cleanup prematurely
                    });
            }
            // Leave Condition: Status indicates ending/completion/error, and currently joined/joining
            else if ((status === 'ending' || status === 'completed' || status === 'error') && (currentState === 'joined-meeting')) {
                console.log(`Daily Effect: Conditions met, leaving call (Status: ${status}, Daily State: ${currentState})`);
                callObject.leave()
                    .then(() => {
                        console.log('Daily Effect: Left meeting successfully.');
                        setDailyJoined(false); // Update local state
                    })
                    .catch(err => console.error('Daily Effect: Error leaving meeting:', err)); // Log error but usually proceed with state change
            }
            // Condition to handle showing main UI *before* joining Daily (e.g., confirming modal)
            else if (showVideoUI && !dailyJoined && (currentState === 'new' || currentState === 'left-meeting')) {
                // If we show the main UI but haven't joined Daily yet (e.g., after confirmation modal)
                // And if the AI has already joined, trigger the join
                if (status === 'active' && AiJoined && conversationUrl) {
                    console.log("Daily Effect: Main UI shown, AI ready, triggering join.");
                    // Join logic is handled by the first `if` condition, this block might be redundant
                    // Or could be used to trigger join explicitly *after* confirmation,
                    // but the main logic should cover it once `showVideoUI` is true *and* `AiJoined` is true.
                    // Ensure the state transitions allow the first `if` block to execute correctly.
                } else {
                    console.log("Daily Effect: Main UI shown, but not ready to join Daily yet.", { status, AiJoined, conversationUrl });
                }
            }


        }, [status, AiJoined, conversationUrl, callObject, showVideoUI, dailyJoined, selectedAudioInputId, selectedVideoId, selectedAudioOutputId, userId]); // Add relevant device IDs if needed for setting after join


        // Function to stop the local media stream used for preview/waiting room
        const stopLocalMediaStream = useCallback(() => {
            console.log("Stopping local media stream (if active)...");
            if (mediaStream) {
                mediaStream.getTracks().forEach((track) => {
                    console.log(`Stopping local track: ${track.kind} (${track.label})`);
                    track.stop();
                });

                setMediaStream(null); // Clear the stream state
            }
            if (videoRef.current) {
                videoRef.current.srcObject = null; // Clear video element source
            }
            // Reset access states
            setCameraAccessGranted(false);
            setMicAccessGranted(false);
        }, [mediaStream]); // Dependency on mediaStream

        // End Conversation Logic
        const endConversation = useCallback(async (reason: string) => {
            console.log(`Action: endConversation triggered. Reason: ${reason}. Current status: ${status}`);
            setIsLoadingChangeInterviewStatus(true);

            setStatus('ending'); // Set intermediate state immediately

            // --- Explicit Media Cleanup ---
            stopLocalMediaStream(); // Stop the preview/waiting room stream

            // Stop Daily recording *before* leaving
            if (callObject && status == 'active') {
                try {
                    console.log("Daily: Stopping recording...");
                    await callObject.stopRecording();
                    // set a delayed request to call generateCandidateInsights and pass conversationId
                    setTimeout(() => {
                        console.log("Action: Generating candidate insights...");
                        generateCandidateInsights({
                            "conversation_id": conversationId as string,
                            "interview_segments": [
                                {
                                    "duration": 180,
                                    "context": "[Resume Overview]\nObjective: Summarize the candidate's background, focusing on their most recent role and their reasons for seeking a new opportunity."
                                },
                                {
                                    "duration": 240,
                                    "context": "[Deep Dive on Problem Solving / Projects]\nObjective: Assess problem-solving and project management skills by examining a specific project."
                                },
                                {
                                    "duration": 180,
                                    "context": "[Technical Skills Deep Dive]\nObjective: Evaluate the candidate's technical proficiency based on their resume."
                                },
                                {
                                    "duration": 180,
                                    "context": "[How They Think & Learn]\nObjective: Evaluate the candidate's learning process and use of modern tools."
                                },
                                {
                                    "duration": 240,
                                    "context": "[Team Fit & Collaboration]\nObjective: Assess the candidate's collaboration style and team fit."
                                },
                                {
                                    "duration": -1,
                                    "context": "[Aspirations & Match]\nObjective: Understand the candidate's career goals and if they align with the opportunity."
                                }
                            ]
                        })
                            .then(() => {
                                console.log("Action: Successfully Queued Interview Insights.");
                            })
                            .catch((err) => {
                                catchErrorLog(`Action: Failed to generate candidate insights: ${err}`);
                            }).finally(() => {
                                setIsLoadingChangeInterviewStatus(false);
                            })
                    }, 30000) // delay 30 seconds to allow recording to finalize
                    console.log("Daily: Recording stopped.");
                } catch (err) {
                    console.error("Daily: Error stopping recording:", err);
                }
                // Leave the Daily call (this also stops Daily's media tracks)
                try {
                    console.log("Daily: Leaving call...");
                    await callObject.leave();
                    console.log("Daily: Left call successfully.");
                    setDailyJoined(false);
                } catch (err) {
                    console.error("Daily: Error leaving call:", err);
                }
            }
            // --- End Explicit Media Cleanup ---
            if (reason === 'candidate_join_timeout') {
                console.log("Timer expired, updating status to 'None'.");
                updateInterviewStatusV2({ new_status: "Pending Interview" }).finally(() => {
                    setIsLoadingChangeInterviewStatus(false);
                })
            }
            else {
                let userInterviewStatus: string = 'Complete';
                // interview time duration < 25% of 20 minutes (< 5 minutes)
                if (interviewTimeDuration < MIN_DURATION_25_PERCENT) {
                    userInterviewStatus = 'Aborted';
                }
                // interview time duration >= 25% and <= 50% of 20 minutes (5-10 minutes)
                else if (interviewTimeDuration >= MIN_DURATION_25_PERCENT && interviewTimeDuration <= MIN_DURATION_50_PERCENT) {
                    userInterviewStatus = 'Partial';
                }
                console.log(`Setting status as ${userInterviewStatus} in ${interviewTimeDuration}.`);
                updateInterviewStatusV2({ new_status: userInterviewStatus }).then(() => {
                    onCompletedInterview();
                    setCurrentInterviewStatus(userInterviewStatus);
                    if (onInterviewStatusChange) {
                        onInterviewStatusChange(userInterviewStatus);
                    }

                    if (handleEndSession) {
                        handleEndSession();
                    }
                }).finally(() => {
                    setIsLoadingChangeInterviewStatus(false);
                    catchErrorLog(`Interview status ${userInterviewStatus} in duration ${interviewTimeDuration} seconds from user: ${userId}.`)
                });
            }

            // Stop local speech detection if active
            if (speech_status === 'listening' || speech_status === 'initializing') {
                console.log("Manually stopping speech detection during endConversation.");
                stopListening();
            }

            setStatus('completed');
            console.log("Action: Status set to 'completed'.");

            // 4. Delayed state reset (allow time for cleanup effects)
            setTimeout(() => {
                console.log("Cleanup: Resetting non-essential state.");
                // Reset states that should clear for a potential next interview
                setConversationUrl(null);
                setAiJoined(false);
                setDailyJoined(false);
                setRequestSubmitted(false);
                setRequestType(null);
                setCountdownActive(false);
            }, 500);
        }, [ // Dependencies
            status, conversationId, authToken, pipecatApiUrl, callObject, stopLocalMediaStream, interviewStartTime, userId, interviewTimeDuration // Correct dependency
        ]);

        // Effect: Trigger Conversation Creation Once Ready
        useEffect(() => {
            // Create conversation only when:
            // - Not blocked by initial status check
            // - API key and relevant IDs are present
            // - Conversation hasn't been created yet
            // - Status is 'idle' (initial state)
            if (!blockInterview && authToken && agentName && !conversationCreatedRef.current && status === 'idle') {
                console.log("Effect: Triggering conversation creation.");
                createConversation();
            }
        }, [blockInterview, authToken, agentName, status, createConversation, interviewExpired]); // Dependencies

        useEffect(() => {
            if (!!import.meta.env.VITE_AI_INTERNSHIP_JOB_ID && showButtonAIInternship) {
                getJobDetail(import.meta.env.VITE_AI_INTERNSHIP_JOB_ID || "").then((res) => {
                    console.log("AI Internship Job Details:", res);
                    setJobAIInternship(res);
                }).finally(() => {
                    setLoadingAIInternship(false);
                })
            }
        }, [showButtonAIInternship])

        // Imperative Handle for external control (e.g., parent component ending interview)
        useImperativeHandle(ref, () => ({
            endInterview: (reason: string = 'manual_external_request') => {
                console.log("Imperative Handle: endInterview called with reason:", reason);
                endConversation(reason);
            },
        }), [endConversation]); // Dependency on the memoized endConversation

        // --- Button Logic & UI Helpers ---
        const canStartInterview = cameraAccessGranted && micAccessGranted && AiJoined && dailyJoined; // Requires user media, AI joined, *and* user joined Daily

        const getButtonTooltip = (): string | undefined => {
            if (canStartInterview) return undefined; // No tooltip needed if enabled
            if (blockInterview) return "Interview blocked. Please check status or request retake.";
            if (!cameraAccessGranted || !micAccessGranted) return "Please grant camera and microphone permissions.";
            if (!AiJoined) return "Waiting for the AI interviewer to join the session.";
            if (!dailyJoined) return "Connecting to the video session..."; // Or "Click Join Session"?
            return "Please ensure media permissions are granted and AI has joined."; // Fallback
        };

        // --- Event Handlers ---
        const handleStartInterviewClick = () => {
            if (!cameraAccessGranted || !micAccessGranted) {
                console.warn("Start clicked but media permissions not granted.");
                setInternalErrorMessage("Please grant camera and microphone access first.");
                return;
            }

            if (blockInterview) {
                console.warn("Start clicked but interview is blocked.");
                // Error message should already be set by checkInterviewStatus
                return;
            }
            if (!AiJoined) {
                console.warn("Start clicked but AI has not joined yet.");
                setInternalErrorMessage("Please wait for the AI interviewer to join.");
                return;
            }
            // Showconfirmation modal
            setShowConfirmationModal(true);

            if (handleStarted) {
                handleStarted(`Audio: ${audioInputName} | Video: ${videoName}`)
            }
        };

        const handleConfirmInterview = async () => {
            trackEvent('ai_interview_started', {
                job_id: queryParams.get("ref") || jobid || "",
                device_type: `Audio: ${audioInputName} | Video: ${videoName}`
            })

            if (callObject) {
                stopListening(); // Stop local speech detection if active
                callObject?.startRecording({
                    type: "cloud",
                    fps: 30,
                    videoBitrate: 5000,
                    layout: {
                        preset: "custom",
                        composition_params: {
                            "mode": "single",
                            "showTextOverlay": false,
                            "showImageOverlay": true,
                            "showBannerOverlay": false,
                            "showWebFrameOverlay": false,
                            "showSidebar": false,
                            "showTitleSlate": false,
                            "enableAutoOpeningSlate": false,
                            "videoSettings.maxCamStreams": 1,
                            "videoSettings.preferScreenshare": false,
                            "videoSettings.omitPausedVideo": false,
                            "videoSettings.omitAudioOnly": false,
                            "videoSettings.omitExtraScreenshares": false,
                            "videoSettings.showParticipantLabels": false,
                            "videoSettings.roundedCorners": false,
                            "videoSettings.cornerRadius_gu": 1.2,
                            "videoSettings.scaleMode": "fit",
                            "videoSettings.scaleModeForScreenshare": "fit",
                            "videoSettings.placeholder.bgColor": "rgb(0, 50, 80)",
                            "videoSettings.highlight.color": "rgb(255, 255, 255)",
                            "videoSettings.highlight.stroke_gu": 0.2,
                            "videoSettings.split.margin_gu": 0,
                            "videoSettings.grid.useDominantForSharing": false,
                            "videoSettings.grid.highlightDominant": true,
                            "videoSettings.grid.preserveAspectRatio": false,
                            "image.assetName": "watermark",
                            "image.position": "top-right",
                            "image.zPosition": "foreground",
                            "image.fullScreen": false,
                            "image.fullScreenScaleMode": "fit",
                            "image.aspectRatio": 1.678,
                            "image.height_gu": 3,
                            "image.margin_gu": 1.7,
                            "image.opacity": 0.9,
                            "image.enableFade": true,
                        },
                        session_assets: {
                            'images/watermark': import.meta.env.VITE_WATERMARK_IMG_URL
                        }
                    }
                });
                console.log("Congrats: Starting recording before user joins...");
                // callObject.updateInputSettings({ audio: { processor: { type: "noise-cancellation" } } });
                // console.log("Congrats2: Started noise cancellation");
            }
            setIsRecordingStarting(true);
            // Rest of logic is handles by the on-recording Daily Event
        };


        // Countdown and Retake/Issue Logic
        useEffect(() => {
            // Start countdown timer only when AI joins, user has media, and not already in main UI
            if (AiJoined && canStartInterview && !countdownActive && !showVideoUI && !interviewExpired && !blockInterview) {
                console.log("Starting join countdown timer.");
                setCountdownActive(true);
            }
        }, [AiJoined, canStartInterview, countdownActive, showVideoUI, interviewExpired, blockInterview]); // Dependencies

        const handleTimeout = () => {
            console.log("Interview join timer expired.");
            if (!showVideoUI) { // Only expire if user hasn't joined the main UI yet
                updateInterviewStatusV2({ new_status: "Pending Interview" })
                setInterviewExpired(true);
                setCountdownActive(false);
                setInternalErrorMessage("You took too long to join the interview. Please request to retake the interview.");
                setStatus('error');
                // Optionally end the conversation immediately if timeout occurs
                endConversation('candidate_join_timeout');
            }
        };

        const handleRetakeRequest = (reason: string) => {
            console.log("Retake request submitted with reason:", reason);
            setRequestSubmitted(true);
            setRequestType("retake");
            setIsRetakeDialogOpen(false);
            // Consider ending the conversation if a retake is requested and session is active
            if (conversationId && status !== 'completed' && status !== 'ending') {
                endConversation('retake_requested');
            }

            if (!onReload) {
                setTimeout(() => {
                    window.location.reload(); // For now, just reload to reset state
                }, 2000);
            } else {
                onReload();
            }
        };

        const handleIssueSubmit = (issueType: string, details: string) => {
            console.log("Issue reported:", { userId, issueType, details, conversationId });
            setRequestType("issue");
            setIsIssuesDialogOpen(false);
            // Consider ending the conversation if an issue is reported and session is active
            if (conversationId && status !== 'completed' && status !== 'ending') {
                endConversation(`issue_reported:_${issueType}`);
            }

            trackEvent('ai_interview_having_issue', {
                job_id: queryParams.get("ref") || jobid || "",
                issue_type: issueType,
                detail: details,
                conversation_id: conversationId,
            });

            snackbar.showSuccess("Issue reported to the team. Please retry your interview.");
            if (!onReload) {
                setTimeout(() => {
                    window.location.reload(); // For now, just reload to reset state
                }, 2000);
            } else {
                onReload();
            }
        };

        // Helper to make AI speak (if needed)
        const makeAiSay = (line: string) => {
            if (callObject && conversationId && callObject.meetingState() === 'joined-meeting') {
                console.log("Sending app message to make AI say:", line);
                const interaction = {
                    message_type: "conversation",
                    event_type: "conversation.echo", // Using echo for direct TTS
                    conversation_id: conversationId,
                    properties: {
                        text: line
                    },
                };
                callObject.sendAppMessage(interaction, '*') // Send to all (AI should pick it up)
                console.log("AI 'say' message sent:", interaction);
            } else {
                console.warn("Cannot make AI say: Daily not joined or no conversation ID.", { joined: callObject?.meetingState(), conversationId });
            }
        };

        const endAiCall = () => {
            if (callObject && conversationId && callObject.meetingState() === 'joined-meeting') {
                console.log("Ending a call:");
                const interaction = {
                    message_type: "conversation",
                    event_type: "conversation.end",
                    conversation_id: conversationId,
                    properties: {
                    },
                };
                callObject.sendAppMessage(interaction, '*') // Send to all (AI should pick it up)
                console.log("AI 'end' message sent:", interaction);
                console.log("Setting status as Complete.");
                updateInterviewStatusV2({ new_status: "Complete" }).then(() => {
                    onCompletedInterview();
                })
            } else {
                console.warn("Cannot end AI call: Daily not joined or no conversation ID.", { joined: callObject?.meetingState(), conversationId });
            }
        };

        const updateConversationContext = (context: string) => {
            if (callObject && conversationId) {
                console.log("Updating conversation context:", context);
                const interaction = {
                    message_type: "conversation",
                    event_type: "conversation.overwrite_llm_context", // Using echo for direct TTS
                    conversation_id: conversationId,
                    properties: {
                        context: context
                    },
                };
                callObject.sendAppMessage(interaction, '*') // Send to all (AI should pick it up)
                console.log("Conversation context updated:", interaction);
            } else {
                console.warn("Failed to update context", { joined: callObject?.meetingState(), conversationId });
            }
        };

        // --- Observe Local User Audio Level ---
        const speakingTimerRef = useRef<NodeJS.Timeout | null>(null);

        // Cleanup timer on unmount
        useEffect(() => {
            return () => {
                if (speakingTimerRef.current) {
                    clearTimeout(speakingTimerRef.current);
                }
                if (pauseTimerRef.current) {
                    clearTimeout(pauseTimerRef.current);
                }
            };
        }, []);

        // --- Get Selected Device Names for UI ---
        const getSelectedDeviceNames = () => {
            const audioInput = audioInputDevices.find(d => d.deviceId === selectedAudioInputId);
            const audioOutput = audioOutputDevices.find(d => d.deviceId === selectedAudioOutputId);
            const video = videoDevices.find(d => d.deviceId === selectedVideoId);

            localStorage.setItem('preferredAudioInputId', audioInput?.label || '');
            localStorage.setItem('preferredAudioOutputId', audioOutput?.label || '');
            localStorage.setItem('preferredVideoId', video?.label || '');
            return {
                audioInputName: audioInput?.label || (selectedAudioInputId ? 'Selected Mic' : 'Default Mic'),
                audioOutputName: audioOutput?.label || (selectedAudioOutputId ? 'Selected Speaker' : 'Default Speaker'),
                videoName: video?.label || (selectedVideoId ? 'Selected Camera' : 'Default Camera')
            };
        };

        const { audioInputName, audioOutputName, videoName } = getSelectedDeviceNames();

        // --- Daily Event Handling ---
        const handleAppMessage: DailyAppMessageCallback = useCallback(
            (event: DailyEventObjectAppMessage) => {
                const pipecat_event = event.data as PipecatAppMessageData

                // check if bot has decided to end the call
                if (pipecat_event.type === "conversation.end") {
                    // setIsConcluding(true);
                    setIsConversationEnded(true);
                    catchErrorLog(`Conversation ended by AI in seconds ${interviewTimeDuration} from user: ${userId}.`);
                }

                if (pipecat_event.type === "bot-tts-started") {
                    // Bot is streaming text
                    setIsAiSpeaking(true);
                }
                else if (pipecat_event.type === "bot-stopped-speaking") {
                    // Bot has finished speaking
                    setTimeout(() => {
                        setIsAiSpeaking(false)
                    }, 2000)
                }

                const tavusMessage = event.data as TavusAppMessageData;
                if (!tavusMessage || !tavusMessage.event_type) return;

                switch (tavusMessage.event_type) {
                    case "conversation.utterance":
                        if (tavusMessage.properties?.role === 'user') {
                            setPauseState(0);
                        }
                        break;
                    case "conversation.replica.started_speaking":
                        console.log("AI started speaking:", tavusMessage.properties);
                        break;
                    case "conversation.replica.stopped_speaking":
                        console.log("Updating last utterance time to:", Date.now());
                        break;
                    default:
                        console.warn(
                            "VideoContainer: Unhandled Tavus Interaction type:",
                            tavusMessage.event_type
                        );
                }
            },
            [conversationStarted]
        );

        useDailyEvent("app-message", handleAppMessage);

        useDailyEvent("participant-joined", useCallback((event) => {
            console.log("Participant Joined:", event.participant);
            setAiJoined(true);
        }, []));

        useDailyEvent("participant-left", useCallback((event) => {
            console.log("Participant left:", event.participant);
            catchErrorLog(`Participant left: ${event.participant.session_id} in seconds ${interviewTimeDuration} from user: ${userId}.`);
            setIsConcluding(true)
        }, []));

        useDailyEvent("recording-started", useCallback((event) => {
            console.log("Recording started:", event);
            setShowConfirmationModal(false);

            // Create the interview record in backend *before* showing the main UI
            if (conversationId && callObject) {
                console.log("Creating interview record for conversation:", conversationId);
                try {
                    // Start recording now that user has joined
                    console.log("Daily Effect: Starting recording after join...");
                    callObject.setLocalAudio(true);

                    // Mark interview start time
                    setInterviewStartTime(Date.now());

                    // Only proceed if creation was successful
                    setShowVideoUI(true); // Show the main VideoContainer UI
                    setIsRecordingStarting(false);

                    // Set initial context (Step 0)
                    console.log("Setting initial interview context (Step 0)");
                    updateConversationContext(interviewContexts[0]);
                    setInterviewStep(0); // Explicitly set step to 0
                    // wait 1 second
                    setTimeout(() => {
                        makeAiSay(`Hey, thanks for joining the call today. Are you ready for your ourteam interview?`)
                    }, 1000)

                } catch (error: any) {
                    console.error("Failed to create interview record:", error);
                    setInternalErrorMessage(`Error starting interview: ${error.message}. Please try again.`);
                }

            } else {
                console.error("Cannot confirm interview: conversationId is missing.");
                setInternalErrorMessage("Cannot start interview: Session ID missing.");
            }
        }, [conversationId, callObject, interviewContexts, userName, isRecordingStarting]));

        // Effect to progress interview steps automatically
        useEffect(() => {
            // Array of step durations in seconds. Index `i` corresponds to duration *after which* to move *to* step `i`.
            // We use indices 1 through 6 for scheduling transitions *to* steps 1 through 6 (or end).
            const stepDurations = [0, 180, 240, 180, 180, 240, 180]; // Changed first value from 0 to 5 seconds

            let timeoutId: NodeJS.Timeout | null = null;
            console.log("showVideoUI:", showVideoUI, "callObject:", !!callObject, "conversationId:", conversationId, "meetingState:", callObject ? callObject.meetingState() : 'N/A');

            // Only run the timer when the main video UI is shown and the call is active
            if (showVideoUI && callObject && conversationId && callObject.meetingState() === 'joined-meeting') {
                const currentStep = interviewStep;
                const nextStep = currentStep + 1;

                console.log(`Interview step effect running. Current step: ${currentStep}`);

                // Check if there is a next step context
                if (nextStep < interviewContexts.length) {
                    // Get the duration for the *current* step (time until we move to the *next* step)
                    // Use stepDurations[nextStep] because stepDurations[1] is duration for step 0, etc.
                    const durationInSeconds = stepDurations[nextStep];

                    if (durationInSeconds > 0) {
                        console.log(`Scheduling transition from step ${currentStep} to step ${nextStep} in ${durationInSeconds} seconds.`);

                        timeoutId = setTimeout(() => {
                            console.log(`Timer fired: Advancing from step ${currentStep} to step ${nextStep}.`);
                            // Update context to the next step's context
                            updateConversationContext(interviewContexts[nextStep]);
                            // Update the state to reflect the new current step
                            setInterviewStep(nextStep);
                        }, durationInSeconds * 1000);

                    } else {
                        console.log(`Duration for step ${currentStep} is 0 or invalid (${durationInSeconds}s). Not scheduling automatic advance to step ${nextStep}.`);
                        // If duration is 0, you might want to advance immediately or handle differently
                        // For now, it just won't schedule the next step automatically via timer.
                    }
                } else {
                    // We have completed the last step (index interviewContexts.length - 1)
                    // Check the duration associated with the *last* step (stepDurations[interviewContexts.length])
                    const lastStepDuration = stepDurations[interviewContexts.length]; // e.g., stepDurations[6] for 6 contexts (0-5)
                    if (lastStepDuration > 0) {
                        console.log(`Last step (${currentStep}) reached. Scheduling end of interview in ${lastStepDuration} seconds based on its duration.`);
                        timeoutId = setTimeout(() => {
                            console.log("Timer fired after last step's duration. Ending conversation.");
                            // Assuming the last context handles the closing remarks,
                            // we might just end the conversation after its duration.
                            endAiCall()
                        }, lastStepDuration * 1000);
                    } else {
                        console.log(`Last step (${currentStep}) reached. No specific duration to wait before ending, or duration is 0.`);
                        // Consider if you want to end immediately or rely on the last context's flow.
                        // Perhaps the last context itself should trigger the end via an app message?
                    }
                }
            }

            // Cleanup function: Clear the timeout when the effect re-runs or component unmounts
            return () => {
                if (timeoutId) {
                    console.log("Clearing interview step timer.");
                    clearTimeout(timeoutId);
                }
            };
            // Dependencies: Only re-run when interviewStep changes
            // Note: callObject removed to prevent re-triggering, we check it exists in condition
        }, [showVideoUI, conversationId, interviewStep]);

        // Add this new useEffect for comprehensive media cleanup on unmount
        useEffect(() => {
            // This effect is only for cleanup when component unmounts
            return () => {
                console.log("Component unmounting: Cleaning up resources");

                // Stop all tracks in the mediaStream state
                if (mediaStream) {
                    mediaStream.getTracks().forEach(track => {
                        console.log(`Stopping ${track.kind} track: ${track.label}`);
                        track.stop();
                    });
                    setMediaStream(null);
                }

                // Also check for any streams directly attached to video element
                if (videoRef.current && videoRef.current.srcObject instanceof MediaStream) {
                    const videoElementStream = videoRef.current.srcObject as MediaStream;
                    videoElementStream.getTracks().forEach(track => {
                        console.log(`Stopping video element ${track.kind} track: ${track.label}`);
                        track.stop();
                    });
                    videoRef.current.srcObject = null;
                }

                // Release media states
                setCameraAccessGranted(false);
                setMicAccessGranted(false);

                // Clear Daily call if still active
                if (callObject && callObject.meetingState() !== 'left-meeting') {
                    console.log("Leaving Daily call on unmount");
                    callObject.leave()
                        .then(() => {
                            console.log("Successfully left Daily call, destroying Daily instance");
                            // After leaving, fully destroy the Daily instance to release all resources
                            callObject.destroy().catch(err => console.error("Error destroying Daily instance:", err));
                        })
                        .catch(err => {
                            console.error("Error leaving call on unmount:", err);
                            // Try to destroy anyway if leaving fails
                            callObject.destroy().catch(err => console.error("Error destroying Daily instance:", err));
                        });
                } else if (callObject) {
                    // If we're already left but need to clean up the Daily instance
                    console.log("Daily call not active, destroying Daily instance");
                    callObject.destroy().catch(err => console.error("Error destroying Daily instance:", err));
                }

                // Stop local speech detection if active
                if (speech_status === 'listening' || speech_status === 'initializing') {
                    console.log("Stopping speech detection on unmount");
                    stopListening();
                }
            };
        }, []); // Empty dependency array ensures this only runs on mount/unmount

        const onHandleEndConfirmInterview = useCallback((isDontShowAgain: boolean) => {
            // If interview duration is less than 50% of target (10 minutes), set status accordingly
            if (interviewTimeDuration <= MIN_DURATION_50_PERCENT) {
                endConversation('interview_too_short');
            } else {
                endConversation('user_request')
            }

            if (isDontShowAgain) {
                setCookie('is-skip-confirm-end-interview', "true", 7) // expired 7 days
            }
            setIsDialogConfirmEndInterview(false);
        }, [interviewTimeDuration]);

        const handleDialogConfirmEndInterview = useCallback(() => {
            const isDontShowAgainConfirmEndInterview = getCookie("is-skip-confirm-end-interview") || false;

            if (isDontShowAgainConfirmEndInterview) {
                if (interviewTimeDuration <= MIN_DURATION_50_PERCENT) {
                    endConversation('interview_too_short');
                } else {
                    endConversation('user_request')
                }
                return;
            }

            if (currentInterviewStatus === 'Aborted' || currentInterviewStatus === 'Partial') {
                setIsDialogConfirmEndInterview(true);
            } else {
                endConversation('user_request');
            }
        }, [interviewTimeDuration, endConversation])

        if (isLoadingChangeInterviewStatus) {
            return (
                <div className="flex flex-col items-center justify-center h-full space-y-4">
                    <p className="text-gray-700 dark:text-gray-300">
                        Updating interview status, please wait...
                    </p>
                </div>
            )
        }

        // --- Render Logic ---
        return (
            <>
                <DailyAudio
                    ref={audioRef as any} // Type assertion to avoid TS error
                    onPlayFailed={handleAudioPlayFailed}
                    maxSpeakers={3} // Adjust as needed
                />

                {showVideoUI ? (
                    <VideoContainer
                        status={status}
                        interviewTitle={"General Introductory Interview"}
                        onEndInterview={handleDialogConfirmEndInterview} // Show end confirmation dialog
                        tavusCallbacks={tavusCallbacks as TavusInteractionCallbacks} // Pass interaction callbacks
                        onCloseDialogSuccessRecord={onCloseModal} // Callback when interview success dialog is closed
                        onIssueReport={() => setIsIssuesDialogOpen(true)} // Pass issue report handler
                        isInterviewConcluding={isConcluding} // Pass the concluding state
                        isConversationEnded={isConversationEnded} // Pass whether the conversation has ended
                        onConcluding={setIsConcluding}
                        concludingCountdown={countdownSeconds} // Pass the countdown timer value
                        interviewStartTime={interviewStartTime as number} // Pass the interview duration
                        userId={userId} // Pass the user ID
                        currentCameraId={selectedVideoId}
                        currentMicrophoneId={selectedAudioInputId}
                        conversationId={conversationId as string} // Pass the conversation ID
                        audioStatus={audioStatus}
                    />
                ) : (
                    // waiting room UI
                    <div className="w-full max-w-6xl" id="waiting-room">
                        <div className="bg-yellow-50 sm:hidden flex text-sm dark:bg-yellow-900/20 border border-yellow-200 rounded-lg px-2 py-1 text-yellow-800 dark:text-yellow-300 items-center mb-2">
                            Looks like you’re on your phone—switch to a laptop or desktop for a smoother experience.
                        </div>
                        {/* Browser compatibility warning - moved above the header */}
                        {!isBrowserSupported && (
                            <BrowserCompatibilityWarning />
                        )}
                        <div className="flex flex-col sm:flex-row justify-between items-start">
                            <div className="flex flex-col">
                                <h1 className="text-xl sm:text-3xl font-semibold sm:font-bold text-gray-900 dark:text-white mb-2">
                                    General Introductory Interview
                                </h1>
                                <div className="text-gray-600 dark:text-gray-400 mb-6 flex flex-row items-center gap-2">
                                    <div className="flex-shrink-0">
                                        <span className="text-sm font-medium text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-full">
                                            {TARGET_INTERVIEW_DURATION / 60} min
                                        </span>
                                    </div>
                                    <div>
                                        <p className="md:ml-2 text-sm">
                                            This interview is designed as a short introduction to help us get to know you. <br />
                                            Please use Chrome for the best interview experience.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-[1.2fr,1fr] gap-8">
                            {/* Left side - Video preview & Controls */}
                            <div className="flex flex-col gap-2">
                                {/* Video Preview Area */}
                                <div className="rounded-xl border text-card-foreground shadow overflow-hidden h-[400px] bg-gray-100 dark:bg-gray-800 relative ">
                                    <video
                                        ref={videoRef}
                                        autoPlay
                                        muted
                                        playsInline
                                        className="w-full h-full object-cover"
                                    />

                                    {/* "You" label with audio visualizer */}
                                    <div className="absolute bottom-3 left-3">
                                        {status !== 'connecting' && mediaStream && (
                                            <div className="text-sm font-medium text-white bg-black/60 px-3 py-1.5 rounded-lg flex flex-row items-center gap-2">
                                                You
                                                {speech_status === 'listening' && audioStatus === 'weak' ? (
                                                    <Tooltip
                                                        content={(
                                                            <p className="text-xs">Weak audio detected, try adjusting your microphone settings.</p>
                                                        )}
                                                    >
                                                        <button
                                                            onClick={() => setIsMicDropdownOpen(true)}
                                                            className="sm:flex items-center hover:opacity-80 transition-opacity hidden"
                                                        >
                                                            <div className="relative h-4 w-4 bg-yellow-500 rounded-full flex items-center justify-center">
                                                                <span className="text-[10px] font-bold text-gray-900 leading-none">!</span>
                                                            </div>
                                                        </button>
                                                        <button
                                                            onClick={() => setIsOpenDialogMic(true)}
                                                            className="flex items-center hover:opacity-80 transition-opacity sm:hidden"
                                                        >
                                                            <div className="relative h-4 w-4 bg-yellow-500 rounded-full flex items-center justify-center">
                                                                <span className="text-[10px] font-bold text-gray-900 leading-none">!</span>
                                                            </div>
                                                        </button>
                                                    </Tooltip>
                                                ) : (isTalking) && (
                                                    <EnhancedAudioVisualizer
                                                        isActive={isTalking}
                                                        variant="wave"
                                                        barCount={4}
                                                        color="green"
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="absolute bottom-3 right-3 sm:hidden block">
                                        <div className="flex flex-row sm:hidden items-center opacity-80 gap-1 bg-black/70 text-white  px-2 py-1.5 rounded-full shadow-sm w-fit">
                                            <ClockIcon className="w-5 h-5" />
                                            <span className="text-xs">20m</span>
                                        </div>
                                    </div>

                                    {/* Loading Indicator */}
                                    {status === 'connecting' && !mediaStream && (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 text-white z-10">
                                            <Loader className="h-8 w-8 animate-spin" />
                                            <span className="mt-2 text-sm">Loading video...</span>
                                        </div>
                                    )}

                                    {/* Overlay if Camera Access Denied/Failed */}
                                    {(!cameraAccessGranted || !micAccessGranted) && (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90 text-white p-4 z-10">
                                            {/* Icon Display */}
                                            <div className="flex flex-row space-x-2 mb-3">
                                                {!cameraAccessGranted && (
                                                    <VideoOffIcon className="h-12 w-12 text-red-400" />
                                                )}
                                                {!micAccessGranted && (
                                                    <MicOffIcon className="h-12 w-12 text-red-400" />
                                                )}
                                            </div>

                                            {/* Message Display */}
                                            <p className="font-semibold mb-1">
                                                {!cameraAccessGranted && !micAccessGranted
                                                    ? "Camera & Microphone Access Needed"
                                                    : !cameraAccessGranted
                                                        ? "Camera Access Needed"
                                                        : "Microphone Access Needed"}
                                            </p>

                                            <p className="text-sm text-center">
                                                Please grant permission in your browser to start the interview.
                                            </p>
                                        </div>
                                    )}

                                    {/* Status indicators */}
                                    <div className="absolute flex flex-row top-3 right-3 z-20 space-x-2">
                                        {/* Mic Status Indicator */}
                                        <div
                                            className={`flex items-center gap-1.5 px-2.5 py-1 opacity-80 rounded-full text-xs font-medium transition-colors backdrop-blur-sm ${micAccessGranted
                                                ? "bg-green-600/70 text-white"
                                                : "bg-red-600/70 text-white"
                                                }`}
                                            title={micAccessGranted ? "Microphone is active" : mediaError?.includes("microphone") ? mediaError : "Microphone access needed or failed"}
                                        >
                                            {micAccessGranted ? (
                                                <MicrophoneIcon className="h-3.5 w-3.5" />
                                            ) : (
                                                <MicOffIcon className="h-3.5 w-3.5" />
                                            )}
                                            <span>{micAccessGranted ? "Mic On" : "Mic Off"}</span>
                                        </div>

                                        {/* Video Status Indicator */}
                                        <div
                                            className={`flex items-center gap-1.5 px-2.5 py-1 opacity-80 rounded-full text-xs font-medium transition-colors backdrop-blur-sm ${cameraAccessGranted
                                                ? "bg-green-600/70 text-white"
                                                : "bg-red-600/70 text-white"
                                                }`}
                                            title={cameraAccessGranted ? "Camera is active" : mediaError?.includes("camera") ? mediaError : "Camera access needed or failed"}
                                        >
                                            {cameraAccessGranted ? (
                                                <VideoIcon className="h-3.5 w-3.5" />
                                            ) : (
                                                <VideoOffIcon className="h-3.5 w-3.5" />
                                            )}
                                            <span>{cameraAccessGranted ? "Camera On" : "Camera Off"}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="sm:hidden grid grid-cols-3 gap-2">
                                    <button onClick={() => setIsOpenDialogMic(true)}>
                                        <MicrophoneSelector
                                            label={audioInputName}
                                            className="text-sm w-full"
                                        />
                                    </button>
                                    <button onClick={() => setIsOpenDialogSpeaker(true)}>
                                        <SpeakerSelector
                                            label={audioOutputName}
                                            className="text-sm w-full"
                                        />
                                    </button>
                                    <button onClick={() => setIsOpenDialogCamera(true)}>
                                        <CameraSelector
                                            label={videoName}
                                            className="text-sm w-full"
                                        />
                                    </button>
                                </div>
                                {/* Device Selectors */}
                                <div className="hidden sm:grid grid-cols-3 gap-2">
                                    {/* Microphone */}
                                    <DropdownMenu open={isMicDropdownOpen} onOpenChange={setIsMicDropdownOpen}>
                                        <DropdownMenuTrigger asChild disabled={audioInputDevices.length === 0}>
                                            <div> {/* Wrap for Tooltip */}
                                                <MicrophoneSelector
                                                    label={audioInputName}
                                                    className="text-sm w-full"
                                                />
                                            </div>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" side="bottom" className="w-[420px] p-0">
                                            <div className="p-2">
                                                {audioInputDevices.map((device) => {
                                                    const isSelected = selectedAudioInputId === device.deviceId;
                                                    return (
                                                        <DropdownMenuItem
                                                            key={device.deviceId}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                handleMicrophoneSelect(device)
                                                            }}
                                                            className="cursor-pointer text-sm"
                                                            data-selected={selectedAudioInputId === device.deviceId ? 'true' : undefined}
                                                        >
                                                            {isSelected && (
                                                                <CheckIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                                            )}
                                                            <span className={cn("flex-1", {
                                                                "text-blue-600 dark:text-blue-400 font-medium": isSelected,
                                                                "text-foreground": !isSelected,
                                                            })}>
                                                                {device.label || `Microphone ${device.deviceId.slice(0, 5)}`}
                                                            </span>
                                                        </DropdownMenuItem>
                                                    )
                                                })}
                                                {audioInputDevices.length === 0 && (
                                                    <DropdownMenuItem disabled>No microphones found</DropdownMenuItem>
                                                )}
                                            </div>
                                            <div className="border-t border-border" />
                                            <div className="p-4">
                                                {/* Header with Status Chip */}
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className="text-sm font-medium text-foreground">Current Audio Levels</span>

                                                    {/* Dynamic Status Chip */}
                                                    {speech_status === 'idle' && audioStatus === 'low' && (
                                                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                                                            <AlertCircleIcon className="h-3 w-3 text-red-600 dark:text-red-400" />
                                                            <span className="text-xs font-medium text-red-600 dark:text-red-400">No audio detected</span>
                                                        </div>
                                                    )}
                                                    {speech_status === 'listening' && audioStatus === 'weak' && (
                                                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                                                            <AlertTriangleIcon className="h-3 w-3 text-yellow-600 dark:text-yellow-400" />
                                                            <span className="text-xs font-medium text-yellow-600 dark:text-yellow-400">Weak audio</span>
                                                        </div>
                                                    )}
                                                    {speech_status === 'listening' && audioStatus === 'strong' && (
                                                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                                                            <CheckCircleIcon className="h-3 w-3 text-green-600 dark:text-green-400" />
                                                            <span className="text-xs font-medium text-green-600 dark:text-green-400">Strong audio</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Microphone Icon with Audio Level Bar */}
                                                <div className="flex items-center gap-3">
                                                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-background border border-border flex items-center justify-center">
                                                        <MicIcon className="h-5 w-5 text-foreground" />
                                                    </div>
                                                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full transition-all duration-100 ease-out ${audioStatus === 'low' ? 'bg-red-500' :
                                                                audioStatus === 'weak' ? 'bg-yellow-500' :
                                                                    'bg-green-500'
                                                                }`}
                                                            style={{ width: `${audioLevel}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </DropdownMenuContent>
                                    </DropdownMenu>

                                    {/* Speaker */}
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild disabled={audioOutputDevices.length === 0}>
                                            <div>
                                                <SpeakerSelector
                                                    label={audioOutputName}
                                                    className="text-sm w-full"
                                                />
                                            </div>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" side="bottom" className="w-[420px] p-0">
                                            {audioOutputDevices.map((device) => {
                                                const isSelected = selectedAudioOutputId === device.deviceId;
                                                return (
                                                    <DropdownMenuItem
                                                        key={device.deviceId}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleSpeakerSelect(device)
                                                        }}
                                                        className="cursor-pointer text-sm"
                                                        data-selected={selectedAudioOutputId === device.deviceId ? 'true' : undefined}
                                                        disabled={!('setSinkId' in HTMLMediaElement.prototype)}
                                                    >
                                                        {isSelected && (
                                                            <CheckIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                                        )}
                                                        <span className={cn("flex-1", {
                                                            "text-blue-600 dark:text-blue-400 font-medium": isSelected,
                                                            "text-foreground": !isSelected,
                                                        })}>
                                                            {device.label || `Speaker ${device.deviceId.slice(0, 5)}`}
                                                        </span>
                                                    </DropdownMenuItem>
                                                )
                                            })}
                                            {audioOutputDevices.length === 0 && (
                                                <DropdownMenuItem disabled>No speakers found</DropdownMenuItem>
                                            )}
                                            {!('setSinkId' in HTMLMediaElement.prototype) && audioOutputDevices.length > 0 && (
                                                <DropdownMenuItem disabled>Speaker selection not supported</DropdownMenuItem>
                                            )}
                                        </DropdownMenuContent>
                                    </DropdownMenu>

                                    {/* Camera */}
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild disabled={videoDevices.length === 0}>
                                            <div>
                                                <CameraSelector
                                                    label={videoName}
                                                    className="text-sm w-full"
                                                />
                                            </div>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" side="bottom" className="w-[420px] p-0">
                                            {videoDevices.map((device) => {
                                                const isSelected = selectedVideoId === device.deviceId;
                                                return (
                                                    <DropdownMenuItem
                                                        key={device.deviceId}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleCameraSelect(device)
                                                        }}
                                                        className="cursor-pointer text-sm"
                                                        data-selected={selectedVideoId === device.deviceId ? 'true' : undefined}
                                                    >
                                                        {isSelected && (
                                                            <CheckIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                                        )}
                                                        <span className={cn("flex-1", {
                                                            "text-blue-600 dark:text-blue-400 font-medium": isSelected,
                                                            "text-foreground": !isSelected,
                                                        })}>
                                                            {device.label || `Camera ${device.deviceId.slice(0, 5)}`}
                                                        </span>
                                                    </DropdownMenuItem>
                                                )
                                            })}
                                            {videoDevices.length === 0 && (
                                                <DropdownMenuItem disabled>No cameras found</DropdownMenuItem>
                                            )}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </div>

                            {/* Right side - Status & Actions */}
                            <div className="flex flex-col gap-6 items-center justify-center w-full">
                                <div className="w-full sm:w-[384px]">
                                    {/* Status Title */}
                                    <div className="w-full">
                                        <h2 className="text-2xl md:text-xl font-semibold mt-0 sm:mt-4 md:mt-0 mb-4">
                                            {requestSubmitted ? "Request Submitted" :
                                                interviewExpired ? "Your AI interview expired" :
                                                    blockInterview ? "Access Blocked" :
                                                        AiJoined ? "Get ready for your AI Interview" :
                                                            (status === 'connecting' || status === 'idle') ? "Connecting..." :
                                                                "Get ready for your AI Interview"}
                                        </h2>
                                        {(!interviewExpired && !requestSubmitted) && (
                                            <div className="space-y-4 mb-6">
                                                <div className="flex items-center gap-3">
                                                    <StarIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                                        Start now or come back later
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <TrendingUpIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                                        Stand out beyond a CV and get noticed faster
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <SettingsIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                                        Check your camera, microphone, and internet connection
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <WifiIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                                        Find a quiet place with stable internet
                                                    </span>
                                                </div>
                                                {/* Timer item - moved to last position */}
                                                {cameraAccessGranted && micAccessGranted && AiJoined && countdownActive && (
                                                    <div className="flex items-center justify-between py-2 border-t border-gray-200 dark:border-gray-700 mt-4 pt-4">
                                                        <div className="flex items-center gap-3">
                                                            <HourglassIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                                                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                                                Time to join:
                                                            </span>
                                                        </div>
                                                        <div className="text-sm font-mono font-bold text-gray-900 dark:text-white">
                                                            {countdownActive && (
                                                                <CountdownTimer
                                                                    initialMinutes={timeoutMinutes}
                                                                    onTimeout={handleTimeout}
                                                                    className="ml-2 inline-block"
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {/* Informational Messages */}
                                        <div className="mb-3">
                                            {/* Request Submitted Message */}
                                            {requestSubmitted && (
                                                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 rounded-lg p-3 w-full">
                                                    <p className="text-sm text-blue-600 dark:text-blue-400">
                                                        We're reviewing your {requestType} request and will email you shortly.
                                                    </p>
                                                </div>
                                            )}

                                            {/* Blocked / Expired Messages */}
                                            {!requestSubmitted && (interviewExpired || blockInterview) && (
                                                <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-lg p-3 w-full">
                                                    <p className="text-sm text-red-600 dark:text-red-400">
                                                        {blockInterview
                                                            ? (internalErrorMessage || "Access blocked. Refreshing may end the session.")
                                                            : (
                                                                <>
                                                                    This interview session has expired.
                                                                    <br />
                                                                    You did not join within the allocated time.
                                                                </>
                                                            )}
                                                    </p>
                                                </div>
                                            )}

                                            {/* Waiting States */}
                                            {!requestSubmitted && !interviewExpired && !blockInterview && (
                                                <>
                                                    {/* Waiting for Permissions */}
                                                    {(!cameraAccessGranted || !micAccessGranted) && (
                                                        <div className="flex items-center justify-center gap-2 text-yellow-600 dark:text-yellow-400">
                                                            <AlertTriangleIcon className="h-4 w-4" />
                                                            <span className="text-sm">Waiting for media permissions...</span>
                                                        </div>
                                                    )}

                                                    {/* Waiting for AI */}
                                                    {cameraAccessGranted && micAccessGranted && !AiJoined && status !== 'connecting' && status !== 'error' && (
                                                        <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400">
                                                            <Loader className="h-4 w-4 animate-spin" />
                                                            <span className="text-sm">Waiting for AI Interviewer...</span>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="w-full space-y-3 sm:space-y-2 flex flex-col items-center mt-4 sm:mt-0 gap-2">
                                            {!requestSubmitted ? (
                                                <>
                                                    {/* Retake Button (Only when expired/blocked) */}
                                                    {(interviewExpired || blockInterview) ? (
                                                        <Button
                                                            onClick={() => setIsRetakeDialogOpen(true)}
                                                            className="bg-blue-600 hover:bg-blue-700 text-white py-6 rounded-full w-full"
                                                        >
                                                            Retake Interview
                                                        </Button>
                                                    ) : (
                                                        /* Start Interview Button */
                                                        <Button
                                                            onClick={handleStartInterviewClick}
                                                            disabled={!cameraAccessGranted || !micAccessGranted || !AiJoined || blockInterview || status === 'connecting' || !isBrowserSupported}
                                                            className={`w-full text-white py-[21px] rounded-full transition-opacity ${(cameraAccessGranted && micAccessGranted && AiJoined && !blockInterview && status !== 'connecting' && isBrowserSupported)
                                                                ? `${!isShowBrand && "bg-blue-600 hover:bg-blue-700"} cursor-pointer`
                                                                : "bg-gray-400 dark:bg-gray-600 cursor-not-allowed opacity-70"
                                                                }`}
                                                            title={getButtonTooltip()}
                                                            style={isShowBrand && companyPreferences ? { backgroundColor: companyPreferences.primary_color } : undefined}
                                                        >
                                                            Start Interview
                                                        </Button>
                                                    )}

                                                    <div className="flex justify-center items-center gap-3 mb-6 w-full">
                                                        {isSkipInterview && (
                                                            <Button
                                                                variant="outline"
                                                                className="w-full bg-transparent border-gray-300 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-full py-[21px]"
                                                                onClick={onSkipInterview}
                                                            >
                                                                I will do this later
                                                            </Button>
                                                        )}
                                                        {!(interviewExpired || blockInterview) && (
                                                            <Button
                                                                variant="outline"
                                                                className="w-full bg-transparent border-gray-300 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-full py-[21px]"
                                                                onClick={() => setIsIssuesDialogOpen(true)}
                                                            >
                                                                I'm having issues
                                                            </Button>
                                                        )}
                                                    </div>

                                                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center leading-relaxed">
                                                        ourteam uses generative AI to conduct the AI interview. <br />
                                                        Your responses are used only to assess your candidacy and are never used to train AI models.
                                                    </p>
                                                </>
                                            ) : (
                                                <Button
                                                    onClick={onCloseModal}
                                                    variant="outline"
                                                    className="w-full rounded-full"
                                                >
                                                    Continue
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div >
                )}

                {
                    showConfirmationModal && (
                        <InterviewConfirmationModal
                            onClose={() => setShowConfirmationModal(false)}
                            onConfirm={handleConfirmInterview}
                            loading={isRecordingStarting}
                            companyName={companyInfo?.company_name}
                        />
                    )
                }

                {isDialogConfirmEndInterview && (
                    <InterviewEndConfirmDialogV2
                        isOpen={isDialogConfirmEndInterview}
                        remainingMinutes={Math.ceil((TARGET_INTERVIEW_DURATION - interviewTimeDuration) / 60)}
                        onClose={() => setIsDialogConfirmEndInterview(false)}
                        onConfirm={onHandleEndConfirmInterview}
                    />
                )}

                <RetakeRequestDialogV2
                    isOpen={isRetakeDialogOpen}
                    onClose={() => setIsRetakeDialogOpen(false)}
                    onSubmit={handleRetakeRequest}
                />

                <IssuesDialog
                    userId={userId}
                    isOpen={isIssuesDialogOpen}
                    onClose={() => setIsIssuesDialogOpen(false)}
                    onSubmit={handleIssueSubmit}
                />

                <Dialog open={isOpenDialogMic} onOpenChange={() => setIsOpenDialogMic(false)}>
                    <DialogContent className="sm:max-w-md p-2" closeButtonClassName="top-4 right-4">
                        <DialogHeader className="flex flex-col text-left pt-4 pl-2">
                            <DialogTitle className="text-xl font-semibold">Select Microphone</DialogTitle>
                        </DialogHeader>
                        <div>
                            {audioInputDevices.map((device) => {
                                const isSelected = selectedAudioInputId === device.deviceId;
                                return (
                                    <button
                                        key={device.deviceId}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleMicrophoneSelect(device)
                                        }}
                                        className="cursor-pointer text-sm flex items-center gap-2 p-2"
                                        data-selected={selectedAudioInputId === device.deviceId ? 'true' : undefined}
                                    >
                                        {isSelected && (
                                            <CheckIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                        )}
                                        <span className={cn("flex", {
                                            "text-blue-600 dark:text-blue-400 font-medium": isSelected,
                                            "text-foreground": !isSelected,
                                        })}>
                                            {device.label || `Microphone ${device.deviceId.slice(0, 5)}`}
                                        </span>
                                    </button>
                                )
                            })}
                            {audioInputDevices.length === 0 && (
                                <div>No microphones found</div>
                            )}
                        </div>
                        <div className="border-t border-border" />
                        <div className="p-4">
                            {/* Header with Status Chip */}
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-medium text-foreground">Current Audio Levels</span>

                                {/* Dynamic Status Chip */}
                                {speech_status === 'idle' && audioStatus === 'low' && (
                                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                                        <AlertCircleIcon className="h-3 w-3 text-red-600 dark:text-red-400" />
                                        <span className="text-xs font-medium text-red-600 dark:text-red-400">No audio detected</span>
                                    </div>
                                )}
                                {speech_status === 'listening' && audioStatus === 'weak' && (
                                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                                        <AlertTriangleIcon className="h-3 w-3 text-yellow-600 dark:text-yellow-400" />
                                        <span className="text-xs font-medium text-yellow-600 dark:text-yellow-400">Weak audio</span>
                                    </div>
                                )}
                                {speech_status === 'listening' && audioStatus === 'strong' && (
                                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                                        <CheckCircleIcon className="h-3 w-3 text-green-600 dark:text-green-400" />
                                        <span className="text-xs font-medium text-green-600 dark:text-green-400">Strong audio</span>
                                    </div>
                                )}
                            </div>

                            {/* Microphone Icon with Audio Level Bar */}
                            <div className="flex items-center gap-3">
                                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-background border border-border flex items-center justify-center">
                                    <MicIcon className="h-5 w-5 text-foreground" />
                                </div>
                                <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                                    <div
                                        className={`h-full transition-all duration-100 ease-out ${audioStatus === 'low' ? 'bg-red-500' :
                                            audioStatus === 'weak' ? 'bg-yellow-500' :
                                                'bg-green-500'
                                            }`}
                                        style={{ width: `${audioLevel}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>

                <Dialog open={isOpenDialogSpeaker} onOpenChange={() => setIsOpenDialogSpeaker(false)}>
                    <DialogContent className="sm:max-w-md p-2" closeButtonClassName="top-4 right-4">
                        <DialogHeader className="flex flex-col text-left pt-4 pl-2">
                            <DialogTitle className="text-xl font-semibold">Select Speaker</DialogTitle>
                        </DialogHeader>
                        {audioOutputDevices.map((device) => {
                            const isSelected = selectedAudioOutputId === device.deviceId;
                            return (
                                <button
                                    key={device.deviceId}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleSpeakerSelect(device)
                                    }}
                                    className="cursor-pointer text-sm flex items-center gap-2 p-2"
                                    data-selected={selectedAudioOutputId === device.deviceId ? 'true' : undefined}
                                    disabled={!('setSinkId' in HTMLMediaElement.prototype)}
                                >
                                    {isSelected && (
                                        <CheckIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                    )}
                                    <span className={cn("flex", {
                                        "text-blue-600 dark:text-blue-400 font-medium": isSelected,
                                        "text-foreground": !isSelected,
                                    })}>
                                        {device.label || `Speaker ${device.deviceId.slice(0, 5)}`}
                                    </span>
                                </button>
                            )
                        })}
                        {audioOutputDevices.length === 0 && (
                            <div>No speakers found</div>
                        )}
                        {!('setSinkId' in HTMLMediaElement.prototype) && audioOutputDevices.length > 0 && (
                            <div>Speaker selection not supported</div>
                        )}
                    </DialogContent>
                </Dialog>

                <Dialog open={isOpenDialogCamera} onOpenChange={() => setIsOpenDialogCamera(false)}>
                    <DialogContent className="sm:max-w-md p-2" closeButtonClassName="top-4 right-4">
                        <DialogHeader className="flex flex-col text-left pt-4 pl-2">
                            <DialogTitle className="text-xl font-semibold">Select Camera</DialogTitle>
                        </DialogHeader>
                        {videoDevices.map((device) => {
                            const isSelected = selectedVideoId === device.deviceId;
                            return (
                                <button
                                    key={device.deviceId}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleCameraSelect(device)
                                    }}
                                    className="cursor-pointer text-sm flex items-start flex-row justify-start gap-2 p-2"
                                    data-selected={selectedVideoId === device.deviceId ? 'true' : undefined}
                                >
                                    {isSelected && (
                                        <CheckIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                    )}
                                    <div className={cn("flex", {
                                        "text-blue-600 dark:text-blue-400 font-medium": isSelected,
                                        "text-foreground": !isSelected,
                                    })}>
                                        {device.label || `Camera ${device.deviceId.slice(0, 5)}`}
                                    </div>
                                </button>
                            )
                        })}
                        {videoDevices.length === 0 && (
                            <div>No cameras found</div>
                        )}
                    </DialogContent>
                </Dialog>
            </>
        )
    });

// Add display name for better debugging
VoiceInterviewV2.displayName = "VoiceInterviewV2";
export default VoiceInterviewV2;